import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Badge,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Tooltip,
  Checkbox,
  Collapse,
  Divider,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useLocation, useNavigate } from 'react-router-dom';
import { Add as AddIcon, AttachFile as AttachFileIcon, CloudDone as CloudDoneIcon, CloudOff as CloudOffIcon, Delete as DeleteIcon, Edit as EditIcon, ErrorOutline as ErrorOutlineIcon, FileDownload as ExportIcon, FileUpload as ImportIcon, OpenInNew as OpenInNewIcon, Save as SaveIcon, Send as SendIcon, PictureAsPdf as PictureAsPdfIcon, PhotoCamera as PhotoCameraIcon, PhotoLibrary as PhotoLibraryIcon, UploadFile as UploadFileIcon, WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { useOneDriveAuth } from '../contexts/OneDriveAuthContext';
import { isCorporateOneDriveConfigured } from '../config/onedriveConfig';
import {
  ensureFolder,
  fetchDriveItemBlob,
  replaceDriveItemContent,
  resolveCorporateDriveId,
  sanitizeForOneDrive,
  uploadFileToFolderById,
} from '../services/onedriveFolderService';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import dataService from '../services/dataService';
import { Project } from '../types/Project';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config/api';
import { liquidationDocNo } from '../utils/liquidationDocNo';
import ScanToPhoneDialog, { type DeliveredReceipt } from './ScanToPhoneDialog';
import ScanBatch, { type LiquidationScanItem, type BatchItemFields } from './ScanBatch';
import { DEFAULT_ROW_SORT, dateOrderItemNumbers, withPhysicalItemNumbers, sortLiquidationRows, receiptRowLabel, makeLiquidationReceiptFilename, type RowSort } from '../utils/liquidation/receiptRows';
import ReceiptCropper from './ReceiptCropper';
import ReceiptUploadCropDialog from './ReceiptUploadCropDialog';
import ReceiptViewer from './ReceiptViewer';
import LiquidationReceiptReview from './LiquidationReceiptReview';
import { arialNarrowBase64 } from '../fonts/arialNarrowBase64';
import { LIQUIDATION_CATEGORIES } from '../data/financeCategories';
import { blobToBase64 } from '../utils/receipts/imageCompress';
import { suggestReceiptCrop } from '../services/receiptCropService';
import { perspectiveCropToBlob, type Quad } from '../utils/receipts/perspectiveCrop';
import { parseReceipt } from '../services/receiptParseService';
import {
  getReceiptParseMimeType,
  isPdfReceipt,
  RECEIPT_FILE_ACCEPT,
  validateReceiptFile,
} from '../utils/receipts/receiptFile';
import MoneyTrailButton from './finance/MoneyTrailButton';
import { financeFocusToken, financeFocusUrl, parseFinanceFocus } from '../utils/financeTraceFocus';
import { liquidationRowOrigin } from '../utils/financeModuleOrigins';
import {
  checkDuplicates,
  computeImageHash,
  describeMatch,
  findLocalDuplicates,
  type DuplicateCheckCandidate,
} from '../services/receiptDuplicateService';

interface LiquidationRow {
  physicalItemNo?: number;
  id: string;
  date: string;
  category: string;
  projectId: string | '';
  projectName: string;
  projectNo: string;
  particulars: string;
  amount: number;
  remarks: string;
  // Write-off (income-tax-deductible business expense). AI-suggested on scan,
  // editable by accounting. Defaults to true for manually-added rows.
  deductible: boolean;
  deductibleReason?: string;
  supplier?: string;
  invoiceNo?: string;
  customerInfoIssues?: string[];
  // Human-readable duplicate-receipt warnings (local + server), set best-effort
  // whenever a receipt is attached to this row. Never blocks saving.
  duplicateWarnings?: string[];
}

// A scanned/photographed receipt attached to an expense row. Files live in
// OneDrive under `Liquidation Receipts/{year}/{form_no}/`; the liquidation doc
// stores only these references (as receipts_json). `file`/`uploadStatus` are
// session-only and never persisted.
interface ReceiptAttachment {
  id: string;
  rowId: string;
  filename: string;
  oneDriveId?: string;
  webUrl?: string;
  thumbnailDataUrl?: string;
  uploadedAt?: string;
  uploadStatus?: 'uploading' | 'done' | 'error';
  file?: File;
  // SHA-256 of the raw image bytes, used for duplicate-receipt detection. Opaque
  // to the server — serialized into receipts_json alongside the other fields.
  imageHash?: string;
}

// ── Image helpers (same approach as ServiceReportTab) ──────────────────────

// Small thumbnail (~120px longest edge, JPEG 0.65) with EXIF orientation baked
// in, so receipts render without a live OneDrive connection after reload.
async function generateThumbnail(source: Blob): Promise<string> {
  const MAX = 120;
  try {
    const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
    const scale = Math.min(1, MAX / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.drawImage(bitmap, 0, 0, w, h);
    bitmap.close();
    return canvas.toDataURL('image/jpeg', 0.65);
  } catch {
    return '';
  }
}

// iPhone HEIC/HEIF → JPEG at attach time so browsers and jsPDF can render it.
async function convertHeicToJpeg(file: File): Promise<File> {
  const name = file.name.toLowerCase();
  const isHeic = name.endsWith('.heic') || name.endsWith('.heif') ||
    file.type === 'image/heic' || file.type === 'image/heif';
  if (!isHeic) return file;
  try {
    const heic2any = (await import('heic2any')).default;
    const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(result) ? result[0] : result;
    const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], jpegName, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

// Bake EXIF orientation into a JPEG data URL so jsPDF embeds it upright.
async function normalizeImageOrientation(source: Blob): Promise<string> {
  try {
    const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.drawImage(bitmap, 0, 0);
    bitmap.close();
    return canvas.toDataURL('image/jpeg', 0.9);
  } catch {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(source);
    });
  }
}

const isImageReceipt = (r: ReceiptAttachment): boolean =>
  (r.file?.type || '').startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp)$/i.test(r.filename);

// Approved cash advances offered in the "Apply to CA" selector. ca_no is the
// human-readable reference (e.g. IOCT2606001-CA01); id stays the real key.
interface CaOption {
  id: string;
  ca_no?: string | null;
  amount: number;
  balance_remaining: number;
  status?: string;
  project_name?: string | null;
  project_no?: string | null;
  purpose?: string | null;
}

interface LoadedLiquidationOption {
  id: string;
  form_no: string;
  date_of_submission: string;
  status: string;
  total_amount: number;
  ca_id?: string | null;
  reimbursement_status?: string | null;
  pending_revision?: unknown;
  workflow_state?: string | null;
  workflow_dates?: { handed_in?: string | null; reviewed?: string | null; reviewed_by?: string | null; reimbursed?: number | null; needs_action?: string | null };
}

// One chip per submitted form showing its current stage (server: server/liquidationWorkflow.js workflowState).
const WORKFLOW_CHIPS: Record<string, { label: string; color: string }> = {
  pending_edit: { label: 'Edit pending', color: '#7b1fa2' },
  awaiting_physical: { label: 'Awaiting paper copy', color: '#757575' },
  review_required: { label: 'Paper copy outdated', color: '#f9a825' },
  awaiting_rj_review: { label: 'Awaiting review', color: '#1976d2' },
  needs_action: { label: 'Needs action', color: '#c62828' },
  awaiting_payment: { label: 'Reimb. pending', color: '#ef6c00' },
};

const workflowChip = (d: LoadedLiquidationOption): { label: string; color: string; tip: string } | null => {
  const dt = d.workflow_dates || {};
  const tip = [
    dt.needs_action && `Needs action: ${dt.needs_action}`,
    dt.handed_in && `Paper handed in ${dt.handed_in}`,
    dt.reviewed && `reviewed${dt.reviewed_by ? ` by ${dt.reviewed_by}` : ''} ${dt.reviewed}`,
    dt.reimbursed && `reimbursed ${new Date(dt.reimbursed * 1000).toLocaleDateString()}`,
  ].filter(Boolean).join(' · ');
  const state = d.workflow_state;
  if (state === 'closed') {
    // Closed with a reimbursement → Reimbursed; CA-settled with nothing owed → Reviewed.
    const reimbursed = d.reimbursement_status === 'reimbursed' || !!dt.reimbursed;
    return { label: reimbursed ? 'Reimbursed' : 'Reviewed', color: '#2e7d32', tip };
  }
  if (state && WORKFLOW_CHIPS[state]) return { ...WORKFLOW_CHIPS[state], tip };
  // Unknown/legacy state: fall back to the old reimbursement chip.
  if (d.pending_revision) return { ...WORKFLOW_CHIPS.pending_edit, tip };
  if (d.reimbursement_status) return d.reimbursement_status === 'reimbursed'
    ? { label: 'Reimbursed', color: '#2e7d32', tip } : { ...WORKFLOW_CHIPS.awaiting_payment, tip };
  return null;
};

// A proposed edit to a submitted liquidation, awaiting superadmin approval.
interface PendingRevisionInfo {
  rows: LiquidationRow[];
  totalAmount: number;
  proposedBy: string;
  proposedByName: string | null;
  proposedAt: number;
  note: string | null;
}

const newRow = (projectName = '', projectNo = ''): LiquidationRow => ({
  id: `row-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  date: new Date().toISOString().slice(0, 10),
  category: '',
  projectId: '',
  projectName,
  projectNo,
  particulars: '',
  amount: 0,
  remarks: '',
  deductible: true,
  supplier: '',
  invoiceNo: '',
});

// Rows with a project sync to project_expenses (COGS); rows with an amount but no
// project (e.g. software subscriptions liquidated without a project) auto-sync to
// overhead_expenses instead of being silently dropped from the P&L.
async function addLiquidationRowsToProjectExpenses(
  rows: LiquidationRow[],
  liquidationId?: string,
  formNo?: string,
  sourceCaId?: string,
  receipts?: ReceiptAttachment[]
): Promise<void> {
  try {
    const token = typeof window !== 'undefined' ? localStorage.getItem('netpacific_token') : null;
    const eligible = rows.filter((r) => Number(r.amount) > 0);
    if (eligible.length === 0) return;
    const hasProject = (r: LiquidationRow) => r.projectId !== '' && r.projectId !== null && r.projectId !== undefined;
    const projectRows = eligible.filter(hasProject);
    const overheadRows = eligible.filter((r) => !hasProject(r));
    // First successfully-uploaded receipt per row (a row can have several; the
    // expense's receiptRef is singular so this is the best-effort pick).
    const receiptByRowId = new Map<string, ReceiptAttachment>();
    (receipts || []).forEach((rec) => {
      if (rec.oneDriveId && rec.webUrl && !receiptByRowId.has(rec.rowId)) receiptByRowId.set(rec.rowId, rec);
    });
    const baseExpense = (r: LiquidationRow): Record<string, unknown> => {
      const expense: Record<string, unknown> = {
        description: formNo
          ? `Liquidation ${formNo}: ${(r.particulars || '').trim() || 'Liquidation'}`
          : (r.particulars || '').trim() || 'Liquidation',
        amount: Number(r.amount),
        date: r.date || new Date().toISOString().slice(0, 10),
        category: (r.category || '').trim() || 'Others',
        sourceType: 'liquidation_sync',
        sourceLiquidationId: liquidationId,
        sourceLiquidationRowId: r.id,
      };
      if (sourceCaId) expense.sourceCaId = sourceCaId;
      const receipt = receiptByRowId.get(r.id);
      if (receipt) {
        expense.receiptRef = { oneDriveId: receipt.oneDriveId, webUrl: receipt.webUrl, filename: receipt.filename };
      }
      // Carry BIR substantiation captured on the row into the tax ledger.
      if ((r.supplier || '').trim()) expense.supplier = (r.supplier || '').trim();
      if ((r.invoiceNo || '').trim()) expense.invoiceNo = (r.invoiceNo || '').trim();
      if (typeof r.deductible === 'boolean') expense.deductible = r.deductible;
      if ((r.deductibleReason || '').trim()) expense.deductibleReason = (r.deductibleReason || '').trim();
      if ((r.remarks || '').trim()) expense.remarks = (r.remarks || '').trim();
      return expense;
    };
    const authHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    await Promise.all([
      projectRows.length === 0 ? Promise.resolve() : fetch(`${API_BASE}/api/project-expenses`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          expenses: projectRows.map((r) => ({
            ...baseExpense(r),
            projectId: r.projectId,
            projectName: (r.projectName || '').trim() || '—',
          })),
        }),
      }),
      overheadRows.length === 0 ? Promise.resolve() : fetch(`${API_BASE}/api/overhead-expenses`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ expenses: overheadRows.map((r) => baseExpense(r)) }),
      }),
    ]);
  } catch (err) {
    console.warn('[LiquidationFormPage] expense sync failed:', err);
  }
}

interface LiquidationRowCardProps {
  row: LiquidationRow;
  index: number;
  projects: Project[];
  lockedRow: boolean;
  receiptCount: number;
  focused: boolean;
  primary: string;
  rowRef?: (el: HTMLDivElement | null) => void;
  onChange: (id: string, field: keyof LiquidationRow, value: string | number | boolean) => void;
  onProject: (id: string, projectId: string | '') => void;
  onScan: (id: string) => void;
  onAttach: (id: string) => void;
  onRemove: (id: string) => void;
  moneyTrail?: React.ReactNode;
}

const peso = (n: number) => `₱${(Number(n) || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;

// Portrait-phone version of one expense line: a compact card (date, category,
// particulars, amount, receipt buttons) that expands into labelled fields.
function LiquidationRowCard({
  row, index, projects, lockedRow, receiptCount, focused, primary, rowRef,
  onChange, onProject, onScan, onAttach, onRemove, moneyTrail,
}: LiquidationRowCardProps) {
  // New, still-empty rows open ready to type in.
  const [open, setOpen] = useState(() => !row.particulars && !row.amount);
  const projectName = projects.find((p) => String(p.id) === String(row.projectId))?.project_name || row.projectName;
  const dateLabel = (() => {
    if (!row.date) return 'No date';
    const d = new Date(`${row.date}T00:00:00`);
    return Number.isNaN(d.getTime()) ? row.date : d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' });
  })();
  const warnings = [...(row.duplicateWarnings ?? []), ...(row.customerInfoIssues ?? [])];
  return (
    <Paper
      ref={rowRef} variant="outlined"
      sx={{
        overflow: 'hidden', flexShrink: 0,
        ...(focused ? { bgcolor: 'rgba(44,90,160,0.14)', outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, p: 1, pr: 0.5 }}>
        <Box onClick={() => setOpen((o) => !o)} sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">Item {row.physicalItemNo ?? '—'}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>{dateLabel}</Typography>
            {row.category && <Chip size="small" label={row.category} variant="outlined" sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }} />}
            {warnings.length > 0 && (
              <Tooltip title={warnings.join(' ')}><WarningAmberIcon fontSize="small" color="warning" /></Tooltip>
            )}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25, overflowWrap: 'anywhere' }}>
            {row.particulars || <Box component="span" sx={{ fontStyle: 'italic', color: 'text.disabled', fontWeight: 400 }}>No particulars</Box>}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 1, mt: 0.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {projectName || 'No project'}{row.supplier ? ` · ${row.supplier}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, whiteSpace: 'nowrap', color: primary }}>{peso(row.amount)}</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <IconButton size="small" onClick={() => onAttach(row.id)} disabled={lockedRow} sx={{ color: primary }} aria-label="Attach receipt">
            <Badge badgeContent={receiptCount} color="primary" overlap="circular"><AttachFileIcon fontSize="small" /></Badge>
          </IconButton>
          <IconButton size="small" onClick={() => setOpen((o) => !o)} aria-label={open ? 'Collapse' : 'Expand to edit'}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Divider />
        <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5 }}>
          <TextField
            size="small" type="date" label="Date" value={row.date} disabled={lockedRow} InputLabelProps={{ shrink: true }}
            onChange={(e) => onChange(row.id, 'date', e.target.value)}
          />
          <TextField
            size="small" type="number" label="Amount" value={row.amount || ''} inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
            onChange={(e) => onChange(row.id, 'amount', parseFloat(e.target.value) || 0)}
          />
          <TextField
            select size="small" label="Category" value={row.category} disabled={lockedRow} sx={{ gridColumn: '1 / -1' }}
            onChange={(e) => onChange(row.id, 'category', e.target.value)}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value=""><em>Select</em></MenuItem>
            {LIQUIDATION_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="Project" value={row.projectId === '' ? '' : String(row.projectId)} disabled={lockedRow}
            sx={{ gridColumn: '1 / -1' }}
            onChange={(e) => onProject(row.id, e.target.value === '' ? '' : String(e.target.value))}
            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
          >
            <MenuItem value=""><em>Select project</em></MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>
                {p.project_name || `Project ${p.id}`}{(p.po_number || p.project_no) ? ` (${p.po_number || p.project_no})` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small" label="PO #" value={row.projectNo}
            onChange={(e) => onChange(row.id, 'projectNo', e.target.value)}
          />
          <TextField
            size="small" label="Invoice No." value={row.invoiceNo || ''}
            onChange={(e) => onChange(row.id, 'invoiceNo', e.target.value)}
          />
          <TextField
            size="small" label="Particulars" value={row.particulars} multiline minRows={3} sx={{ gridColumn: '1 / -1' }}
            onChange={(e) => onChange(row.id, 'particulars', e.target.value)}
          />
          <TextField
            size="small" label="Supplier" value={row.supplier || ''} sx={{ gridColumn: '1 / -1' }}
            onChange={(e) => onChange(row.id, 'supplier', e.target.value)}
          />
          <TextField
            size="small" label="Remarks" value={row.remarks} sx={{ gridColumn: '1 / -1' }}
            onChange={(e) => onChange(row.id, 'remarks', e.target.value)}
          />
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center' }}>
            <Checkbox
              size="small" checked={!!row.deductible} disabled={lockedRow} sx={{ p: 0.5, mr: 0.5 }}
              onChange={(e) => onChange(row.id, 'deductible', e.target.checked)}
              inputProps={{ 'aria-label': 'Deductible' }}
            />
            <Typography variant="body2">Deductible (allowable business expense with a valid BIR receipt)</Typography>
          </Box>
        </Box>
        <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {receiptCount === 0 && (
            <Button size="small" variant="outlined" startIcon={<PhotoCameraIcon />} disabled={lockedRow} onClick={() => onScan(row.id)}>Scan receipt</Button>
          )}
          <Button size="small" variant="outlined" startIcon={<AttachFileIcon />} disabled={lockedRow} onClick={() => onAttach(row.id)}>
            Attach{receiptCount ? ` (${receiptCount})` : ''}
          </Button>
          {moneyTrail}
          <Box sx={{ flex: 1 }} />
          <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onRemove(row.id)}>Delete</Button>
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function LiquidationFormPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [employeeName, setEmployeeName] = useState('');
  const [employeeNumber, setEmployeeNumber] = useState('');
  const [dateOfSubmission, setDateOfSubmission] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [formNo, setFormNo] = useState('');
  const nextPhysicalItemNoRef = useRef(1);
  const newPhysicalRow = (projectName = '', projectNo = ''): LiquidationRow => ({
    ...newRow(projectName, projectNo), physicalItemNo: nextPhysicalItemNoRef.current++,
  });
  const [rows, setRows] = useState<LiquidationRow[]>([]);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<LoadedLiquidationOption[]>([]);
  const [submittedLiquidations, setSubmittedLiquidations] = useState<LoadedLiquidationOption[]>([]);
  const [isViewingSubmitted, setIsViewingSubmitted] = useState(false);
  // Editing a submitted liquidation in place: changes go through the
  // propose-edit endpoint (pending superadmin approval; superadmin applies
  // immediately) instead of creating a new document.
  const [revisionMode, setRevisionMode] = useState(false);
  const [pendingRevision, setPendingRevision] = useState<PendingRevisionInfo | null>(null);
  const [loadedOptionValue, setLoadedOptionValue] = useState<string>('');
  const [reviewContentHash, setReviewContentHash] = useState('');
  const [reviewLocked, setReviewLocked] = useState(false);
  const [cashAdvances, setCashAdvances] = useState<CaOption[]>([]);
  // Reimbursement tracking of the currently loaded submitted no-CA liquidation.
  const [loadedReimb, setLoadedReimb] = useState<{ id: string; status: string | null; at: number | null; caId: string | null } | null>(null);
  // Receipt scans/photos attached per expense row.
  const [receipts, setReceipts] = useState<ReceiptAttachment[]>([]);
  const [receiptCropQueue, setReceiptCropQueue] = useState<{ rowId: string; files: File[] } | null>(null);
  // Portrait phones (< 600 px) show expense lines as cards instead of the wide table.
  const isPhone = useMediaQuery('(max-width:599.95px)');
  const [receiptViewer, setReceiptViewer] = useState<{ receipt: ReceiptAttachment; url: string | null } | null>(null);
  const receiptInputRef = useRef<HTMLInputElement>(null);
  const receiptRowIdRef = useRef<string | null>(null);
  const [scanSnackbar, setScanSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>({ open: false, message: '', severity: 'success' });
  const [scanDialog, setScanDialog] = useState<{ open: boolean; rowId: string | null }>({ open: false, rowId: null });
  const [scanBatchOpen, setScanBatchOpen] = useState(false);
  // Desktop "Scan One" — local file picker (image or PDF), mirrors Expense Monitoring's
  // Scan One: PDFs parse directly (no crop), photos auto-detect corners and open the
  // crop dialog first. Reuses handleScanBatchItem to create the row + attach the receipt.
  const scanOneInputRef = useRef<HTMLInputElement>(null);
  const scanOneGenRef = useRef(0);
  const [isScanningOne, setIsScanningOne] = useState(false);
  const [scanOneEditBlob, setScanOneEditBlob] = useState<Blob | null>(null);
  const [scanOneEditUrl, setScanOneEditUrl] = useState<string | null>(null);
  const [scanOneEditQuad, setScanOneEditQuad] = useState<Quad | null>(null);
  const receiptsFolderRef = useRef<{ key: string; driveId: string; folderId: string } | null>(null);
  // Receipts the user deleted since the last load/save. The server keeps any
  // stored receipt a save leaves out unless it is listed here, so a tab that
  // lost its receipt list can't wipe the saved one (LQ26-024-TJC).
  const removedReceiptIdsRef = useRef<Set<string>>(new Set());
  // Receipt ids as of the last load/save, to warn before leaving with unsaved uploads.
  const savedReceiptIdsRef = useRef<Set<string>>(new Set());
  const { isAuthenticated: oneDriveSignedIn, getAccessToken: getOneDriveToken, login: oneDriveLogin } = useOneDriveAuth();
  const [saving, setSaving] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [caId, setCaId] = useState<string | ''>('');
  const [sortConfig, setSortConfig] = useState<RowSort>(DEFAULT_ROW_SORT);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const financeRowRefs = useRef(new Map<string, HTMLElement>());
  const [financeFocusError, setFinanceFocusError] = useState('');
  const [financeFocusLoading, setFinanceFocusLoading] = useState(false);

  const canDeleteLiquidation = draftId !== null || loadedOptionValue.startsWith('submitted:');
  // Only a submitted liquidation has a real number. A draft's stored form_no is
  // the preview from the day it was created; it still names the draft's receipt
  // folder (submit moves the files from it), but another submit may have taken
  // it since, so it is never shown or printed as the form's number.
  const formNoIsFinal = loadedOptionValue.startsWith('submitted:');
  const [livePreviewNo, setLivePreviewNo] = useState('');
  const liquidationToDeleteId = draftId ?? (loadedOptionValue.startsWith('submitted:') ? loadedOptionValue.split(':')[1] : null);
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const location = useLocation();
  const navigate = useNavigate();
  const rawFinanceFocus = new URLSearchParams(location.search).get('focus') || '';
  const focusedFinanceOrigin = parseFinanceFocus(rawFinanceFocus);
  const loadedLiquidationId = loadedOptionValue.includes(':')
    ? loadedOptionValue.slice(loadedOptionValue.indexOf(':') + 1)
    : '';

  const handleSort = (key: 'date' | 'amount') => {
    setSortConfig((prev) => {
      if (prev?.key === key) return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      return { key, direction: 'asc' };
    });
  };

  const sortedRows = React.useMemo(() => sortLiquidationRows(rows, sortConfig), [rows, sortConfig]);

  // A draft has no paper copy yet, so its item numbers follow date order and
  // re-sort as lines are added or re-dated (the server does the same on save).
  // Submitted forms keep their fixed numbers; a later line takes the next one.
  useEffect(() => {
    if (isViewingSubmitted || revisionMode) return;
    const numbers = dateOrderItemNumbers(rows);
    if (rows.every((row) => row.physicalItemNo === numbers.get(row.id))) return;
    setRows((prev) => {
      const next = dateOrderItemNumbers(prev);
      return prev.map((row) => ({ ...row, physicalItemNo: next.get(row.id) }));
    });
    nextPhysicalItemNoRef.current = rows.length + 1;
  }, [rows, isViewingSubmitted, revisionMode]);

  // Default the Name field to the logged-in user only when it's still empty
  // (fresh form). Must NOT depend on `employeeName` itself — a preparer typing
  // someone else's name (or a loaded draft's employee_name) would otherwise be
  // stomped back to the logged-in user's name on every keystroke/load.
  useEffect(() => {
    if (employeeName) return;
    const fullName = user?.full_name?.trim();
    const fallback = user?.username;
    if (fullName) setEmployeeName(fullName);
    else if (fallback) setEmployeeName(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.full_name, user?.username]);

  useEffect(() => {
    dataService.getProjects().then(setProjects);
  }, []);

  const token = typeof window !== 'undefined' ? localStorage.getItem('netpacific_token') : null;

  // Arriving from the CA form's "Liquidate" button: preselect the CA.
  useEffect(() => {
    const fromQuery = new URLSearchParams(location.search).get('ca_id');
    if (fromQuery) setCaId(fromQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch next form number on mount (for new forms)
  useEffect(() => {
    if (!token || draftId !== null || formNo) return;
    fetch(`${API_BASE}/api/liquidations/next-form-no`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.form_no) {
          setFormNo(d.form_no);
        }
      })
      .catch(() => {});
  }, [token, draftId, formNo]);
  // Refresh the preview whenever an unsubmitted form is opened or saved.
  useEffect(() => {
    if (!token || formNoIsFinal) { setLivePreviewNo(''); return; }
    let cancelled = false;
    fetch(`${API_BASE}/api/liquidations/next-form-no`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (!cancelled && d.success && d.form_no) setLivePreviewNo(d.form_no); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token, formNoIsFinal, draftId, loadedOptionValue]);
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/liquidations`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.liquidations) {
          setDrafts(d.liquidations.filter((l: { status: string }) => l.status === 'draft'));
          setSubmittedLiquidations(d.liquidations.filter((l: { status: string }) => l.status === 'submitted').sort((a: { created_at: number }, b: { created_at: number }) => (b.created_at || 0) - (a.created_at || 0)));
        }
      })
      .catch(() => {});
  }, [token]);
  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/api/cash-advances`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        // Keep the full list — the dropdown filters to usable CAs at render
        // time, but loaded liquidations need to resolve any linked CA's ca_no.
        if (d.success && d.cash_advances) setCashAdvances(d.cash_advances);
      })
      .catch(() => {});
  }, [token]);

  // Get-or-create `Liquidation Receipts/{year}/{form_no}/` in the corporate
  // drive. Cached per form number for the session.
  const ensureReceiptsFolder = async (): Promise<{ driveId: string; folderId: string } | null> => {
    if (!isCorporateOneDriveConfigured() || !oneDriveSignedIn) return null;
    const key = sanitizeForOneDrive((formNo || 'draft').trim() || 'draft');
    if (receiptsFolderRef.current?.key === key) return receiptsFolderRef.current;
    const odToken = await getOneDriveToken();
    if (!odToken) return null;
    const driveId = await resolveCorporateDriveId(odToken);
    const year = String(new Date().getFullYear());
    await ensureFolder(odToken, driveId, '', 'Liquidation Receipts');
    await ensureFolder(odToken, driveId, 'Liquidation Receipts', year);
    const folder = await ensureFolder(odToken, driveId, `Liquidation Receipts/${year}`, key);
    receiptsFolderRef.current = { key, driveId, folderId: folder.id };
    return receiptsFolderRef.current;
  };

  // Mirrors of `rows`/`receipts` for use inside async duplicate-check helpers,
  // which are kicked off right after a `setRows`/`setReceipts` call and would
  // otherwise see a stale closure over the pre-update state.
  const rowsRef = useRef<LiquidationRow[]>(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const receiptsRef = useRef<ReceiptAttachment[]>(receipts);
  useEffect(() => { receiptsRef.current = receipts; }, [receipts]);

  // Best-effort duplicate check for a single row: compares it against every
  // other row already on the form (local, synchronous rules) and against
  // existing Firestore records (server round-trip). Never throws — any
  // failure just means no warning is shown. Non-blocking: the row can still
  // be saved/submitted regardless of the outcome.
  //
  // `targetRow` is passed explicitly by every call site (rather than looked up
  // via rowsRef) so a row added in the same tick — before the rowsRef-sync
  // useEffect has run — is still checked with its real, current fields. `others`
  // is still read off rowsRef.current, since call sites also update the ref
  // synchronously alongside setRows so it reflects any just-added rows too.
  const checkRowForDuplicates = async (targetRow: LiquidationRow, imageHash?: string) => {
    try {
      const rowId = targetRow.id;
      const hasSignal = !!imageHash || !!(targetRow.invoiceNo || '').trim() ||
        (!!(targetRow.supplier || '').trim() && Number(targetRow.amount) > 0);
      if (!hasSignal) return;
      const candidate: DuplicateCheckCandidate = {
        key: rowId,
        supplier: targetRow.supplier || undefined,
        invoiceNo: targetRow.invoiceNo || undefined,
        amount: Number(targetRow.amount) || undefined,
        date: targetRow.date || undefined,
        imageHash: imageHash || undefined,
      };
      const others: DuplicateCheckCandidate[] = rowsRef.current
        .filter((r) => r.id !== rowId)
        .map((r) => ({
          key: r.id,
          supplier: r.supplier || undefined,
          invoiceNo: r.invoiceNo || undefined,
          amount: Number(r.amount) || undefined,
          date: r.date || undefined,
          imageHash: receiptsRef.current.find((rc) => rc.rowId === r.id)?.imageHash || undefined,
        }));
      const localMatches = findLocalDuplicates(candidate, others);
      const localWarnings = localMatches.map((m) => {
        const row = rowsRef.current.find((r) => r.id === m.key);
        const label = row?.particulars || row?.supplier || 'another row';
        return `Looks like the same receipt as "${label}" already on this form.`;
      });
      const serverMatches = await checkDuplicates([candidate]);
      const remoteWarnings = (serverMatches.get(rowId) || []).map(describeMatch);
      const warnings = [...localWarnings, ...remoteWarnings];
      if (warnings.length > 0) {
        setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, duplicateWarnings: warnings } : r)));
        setScanSnackbar({ open: true, severity: 'warning', message: `Possible duplicate receipt: ${warnings[0]}` });
      }
    } catch (err) {
      console.warn('[LiquidationFormPage] duplicate check failed:', err);
    }
  };

  const attachReceiptFile = async (rowId: string, raw: File, precomputedHash?: string, targetRowOverride?: LiquidationRow) => {
    const file = await convertHeicToJpeg(raw);
    const targetRow = targetRowOverride || rowsRef.current.find((r) => r.id === rowId);
    const receiptId = `rcpt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const filename = makeLiquidationReceiptFilename({ formNo, date: targetRow?.date,
      particulars: targetRow?.particulars || targetRow?.supplier, physicalItemNo: targetRow?.physicalItemNo, file, uniqueId: receiptId });
    const thumbnailDataUrl = file.type.startsWith('image/') ? await generateThumbnail(file) : '';
    let imageHash = precomputedHash;
    if (!imageHash && file.type.startsWith('image/')) {
      try {
        imageHash = await computeImageHash(await blobToBase64(file));
      } catch { /* best-effort */ }
    }
    const rec: ReceiptAttachment = {
      id: receiptId,
      rowId,
      filename,
      thumbnailDataUrl: thumbnailDataUrl || undefined,
      uploadStatus: 'uploading',
      file,
      imageHash: imageHash || undefined,
    };
    setReceipts((prev) => [...prev, rec]);
    if (targetRow) void checkRowForDuplicates(targetRow, imageHash);
    try {
      const folder = await ensureReceiptsFolder();
      const odToken = folder ? await getOneDriveToken() : null;
      if (!folder || !odToken) throw new Error('OneDrive not connected');
      const item = await uploadFileToFolderById(odToken, folder.driveId, folder.folderId, rec.filename, file);
      setReceipts((prev) => prev.map((r) => r.id === rec.id
        ? { ...r, uploadStatus: 'done', oneDriveId: item.id, webUrl: item.webUrl, uploadedAt: new Date().toISOString() }
        : r));
    } catch {
      // Kept in memory — still embeds in the PDF, but won't survive reload.
      setReceipts((prev) => prev.map((r) => (r.id === rec.id ? { ...r, uploadStatus: 'error' } : r)));
    }
  };

  const attachReceipts = async (rowId: string, files: FileList | null) => {
    if (!rowId || !files || files.length === 0) return;
    const selected = Array.from(files);
    const images = selected.filter(file => !isPdfReceipt(file));
    if (images.length) setReceiptCropQueue({ rowId, files: images });
    for (const pdf of selected.filter(isPdfReceipt)) await attachReceiptFile(rowId, pdf);
  };
  const advanceReceiptCrop = () => setReceiptCropQueue(current => current && current.files.length > 1
    ? { ...current, files: current.files.slice(1) } : null);

  // Each receipt scanned via the desktop multi-upload batch becomes its own new
  // row (pre-filled from the AI parse) plus an attached receipt — mirroring how
  // `handlePhoneReceipt` below treats receipts delivered from a phone.
  const handleScanBatchItem = async (item: LiquidationScanItem): Promise<boolean> => {
    const f = item.fields;
    const nr: LiquidationRow = {
      ...newPhysicalRow('', ''),
      date: f.date || new Date().toISOString().slice(0, 10),
      category: f.category || '',
      amount: Number(f.amount) > 0 ? Number(f.amount) : 0,
      particulars: f.description || f.supplier || '',
      deductible: typeof f.deductible === 'boolean' ? f.deductible : true,
      deductibleReason: f.deductibleReason || undefined,
      supplier: f.supplier || '',
      invoiceNo: f.invoiceNumber || '',
      customerInfoIssues: f.customerIssues.length ? f.customerIssues : undefined,
    };
    const nextRows = [...rowsRef.current, nr];
    rowsRef.current = nextRows;
    setRows(nextRows);
    const blob = item.croppedBlob ?? item.rawFile;
    const file = blob instanceof File ? blob : new File([blob], item.rawFile.name || `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
    await attachReceiptFile(nr.id, file, item.imageHash, nr);
    return true;
  };

  const setScanOneEdit = (blob: Blob | null, quad: Quad | null) => {
    setScanOneEditUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return blob ? URL.createObjectURL(blob) : null; });
    setScanOneEditBlob(blob);
    setScanOneEditQuad(quad);
  };
  useEffect(() => () => { if (scanOneEditUrl) URL.revokeObjectURL(scanOneEditUrl); }, [scanOneEditUrl]);

  // Parses a single scanned receipt (PDF or cropped photo) and hands it to
  // handleScanBatchItem, which creates the new row and attaches the file.
  const parseAndAddScanOneRow = async (rawFile: File, croppedBlob: Blob | undefined, gen: number) => {
    const blobForParse = croppedBlob ?? rawFile;
    const mimeType = croppedBlob ? 'image/jpeg' : getReceiptParseMimeType(rawFile);
    const base64 = await blobToBase64(blobForParse);
    const parsed = await parseReceipt(base64, mimeType);
    if (gen !== scanOneGenRef.current) return;
    const imageHash = mimeType.startsWith('image/') ? await computeImageHash(base64).catch(() => undefined) : undefined;
    const amt = parsed.total ?? parsed.subtotal;
    const sugCat = parsed.suggestedCategory ?? '';
    const fields: BatchItemFields = {
      amount: typeof amt === 'number' ? String(amt) : '',
      date: parsed.date || '',
      category: (LIQUIDATION_CATEGORIES as readonly string[]).includes(sugCat) ? sugCat : '',
      description: parsed.description || parsed.lineItems?.[0]?.description || parsed.vendor || '',
      supplier: parsed.vendor || '',
      invoiceNumber: parsed.invoiceNumber || '',
      invoiceType: parsed.invoiceType || '',
      vat: typeof parsed.tax === 'number' && parsed.tax > 0 ? String(parsed.tax) : '',
      deductible: typeof parsed.deductible === 'boolean' ? parsed.deductible : null,
      deductibleReason: parsed.deductibleReason || null,
      customerIssues: parsed.customerValidation?.issues ?? [],
      lowConf: typeof parsed.confidence === 'number' && parsed.confidence < 0.5,
      projectId: '',
    };
    await handleScanBatchItem({ rawFile, croppedBlob, fields, imageHash });
    const amtLabel = Number(amt ?? 0).toLocaleString('en-PH', { minimumFractionDigits: 2 });
    setScanSnackbar({
      open: true,
      severity: fields.lowConf ? 'warning' : 'success',
      message: fields.lowConf
        ? `Added row (low confidence — please verify): ${parsed.vendor || 'Unknown vendor'} (₱${amtLabel})`
        : `Added row: ${parsed.vendor || 'Unknown vendor'} (₱${amtLabel})`,
    });
  };

  // Desktop Scan One: PDFs parse directly (no crop); photos auto-detect corners
  // and open the crop dialog first — mirrors ExpenseMonitoring's handleScanInputChange.
  const handleScanOneFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    scanOneGenRef.current += 1;
    const gen = scanOneGenRef.current;
    const validationError = validateReceiptFile(file);
    if (validationError) {
      setScanSnackbar({ open: true, severity: 'error', message: validationError });
      return;
    }
    setIsScanningOne(true);
    try {
      if (isPdfReceipt(file)) {
        await parseAndAddScanOneRow(file, undefined, gen);
        return;
      }
      const safeFile = await convertHeicToJpeg(file);
      const quad = await suggestReceiptCrop(safeFile);
      if (gen !== scanOneGenRef.current) return;
      setScanOneEdit(safeFile, quad);
    } catch (err) {
      if (gen === scanOneGenRef.current) {
        setScanSnackbar({ open: true, severity: 'error', message: err instanceof Error ? err.message : 'Could not process receipt' });
      }
    } finally {
      if (gen === scanOneGenRef.current) setIsScanningOne(false);
    }
  };

  const retakeScanOne = () => {
    setScanOneEdit(null, null);
    scanOneInputRef.current?.click();
  };

  const confirmScanOneCrop = async (quad: Quad) => {
    if (!scanOneEditBlob) return;
    scanOneGenRef.current += 1;
    const gen = scanOneGenRef.current;
    setIsScanningOne(true);
    try {
      const flattened = await perspectiveCropToBlob(scanOneEditBlob, quad);
      const rawFile = scanOneEditBlob instanceof File
        ? scanOneEditBlob
        : new File([scanOneEditBlob], `receipt-${Date.now()}.jpg`, { type: 'image/jpeg' });
      await parseAndAddScanOneRow(rawFile, flattened, gen);
      setScanOneEdit(null, null);
    } catch (err) {
      setScanSnackbar({ open: true, severity: 'error', message: err instanceof Error ? err.message : 'Failed to parse receipt' });
    } finally {
      setIsScanningOne(false);
    }
  };

  const markReceiptsRemoved = (gone: ReceiptAttachment[]) => {
    for (const r of gone) {
      removedReceiptIdsRef.current.add(r.id);
      if (r.oneDriveId) removedReceiptIdsRef.current.add(r.oneDriveId);
    }
  };
  const removeReceipt = (id: string) => {
    markReceiptsRemoved(receiptsRef.current.filter((r) => r.id === id));
    setReceipts((prev) => prev.filter((r) => r.id !== id));
  };
  // Call after a load or a successful save: the server copy now matches `list`.
  const markReceiptsSaved = (list: ReceiptAttachment[]) => {
    removedReceiptIdsRef.current = new Set();
    savedReceiptIdsRef.current = new Set(list.map((r) => r.id));
  };
  // Saving while an upload runs (or after one failed) would leave that file in
  // OneDrive with no link to it. Returns false when the save should not go ahead.
  const receiptsReadyToSave = (): boolean => {
    const current = receiptsRef.current;
    const uploading = current.filter((r) => r.uploadStatus === 'uploading').length;
    if (uploading > 0) {
      setSubmitSuccess(`Wait for ${uploading} receipt upload${uploading === 1 ? '' : 's'} to finish before saving.`);
      return false;
    }
    const failed = current.filter((r) => r.uploadStatus === 'error').length;
    if (failed > 0) {
      return window.confirm(`${failed} receipt${failed === 1 ? '' : 's'} failed to upload and will not be saved. Save anyway? (Cancel to retry or re-attach them first.)`);
    }
    return true;
  };
  // Warn before closing/reloading the tab with uploaded receipts that aren't saved yet.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const unsaved = receiptsRef.current.some((r) => r.uploadStatus === 'uploading'
        || (r.uploadStatus === 'done' && !savedReceiptIdsRef.current.has(r.id)));
      if (!unsaved) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // In-app viewer (with rotate) instead of always jumping out to OneDrive.
  // Prefers the in-memory File (this session's own upload, no network round-trip)
  // and falls back to fetching the OneDrive copy for receipts loaded from a saved liquidation.
  const openReceiptViewer = async (receipt: ReceiptAttachment) => {
    setReceiptViewer({ receipt, url: receipt.file ? URL.createObjectURL(receipt.file) : null });
    if (receipt.file || !receipt.oneDriveId) return;
    try {
      const blob = await fetchDriveItemBlob('server', 'server', receipt.oneDriveId);
      setReceiptViewer((prev) => (prev && prev.receipt.id === receipt.id ? { ...prev, url: URL.createObjectURL(blob) } : prev));
    } catch {
      setScanSnackbar({ open: true, severity: 'error', message: 'Could not load the receipt image. Use "Open in OneDrive" instead.' });
    }
  };
  useEffect(() => () => { if (receiptViewer?.url) URL.revokeObjectURL(receiptViewer.url); }, [receiptViewer]);

  // Bakes the viewer's current rotation into the file and overwrites the same
  // OneDrive item in place (id/webUrl unchanged) — this works even on a
  // submitted (locked) liquidation, since it never touches receipts_json/rows,
  // only the file's bytes in OneDrive. Also refreshes the row's small local
  // thumbnail for this session; that thumbnail persists to Firestore the next
  // time this liquidation is normally saved (draft) or edited (revision).
  const saveReceiptRotation = async (receipt: ReceiptAttachment, rotatedBlob: Blob) => {
    if (!receipt.oneDriveId) throw new Error('This receipt has not finished uploading yet.');
    await replaceDriveItemContent(receipt.oneDriveId, rotatedBlob);
    try {
      const newThumb = await generateThumbnail(rotatedBlob);
      if (newThumb) setReceipts((prev) => prev.map((r) => (r.id === receipt.id ? { ...r, thumbnailDataUrl: newThumb } : r)));
    } catch {
      // best-effort — the row list thumbnail just stays as-is until next re-attach
    }
    setScanSnackbar({ open: true, severity: 'success', message: 'Receipt rotated and saved.' });
  };

  const scanYear = String(new Date().getFullYear());
  const scanFolderPath = `Liquidation Receipts/${scanYear}/${sanitizeForOneDrive((formNo || 'draft').trim() || 'draft')}`;

  // The first receipt of a phone-scan session fills the row the QR was started
  // from; each subsequent receipt auto-creates a NEW row, so the user can keep
  // scanning on the phone without rescanning the QR.
  const scanTargetRef = useRef<{ pairingToken: string; rowId: string; consumed: boolean } | null>(null);

  const [activeScanJob, setActiveScanJob] = useState<{ pairingToken: string; received: number } | null>(null);
  const scanJobInFlightRef = useRef(false);

  const handlePhoneReceipt = (r: DeliveredReceipt, jobToken: string) => {
    const p = r.parsed;
    const applyParsed = (row: LiquidationRow): LiquidationRow => ({
      ...row,
      date: p?.date || row.date,
      category: p?.category || row.category,
      amount: (typeof p?.amount === 'number' && p.amount > 0) ? p.amount : row.amount,
      particulars: (!row.particulars || row.particulars.trim() === '')
        ? (p?.particulars || p?.vendor || row.particulars)
        : row.particulars,
      deductible: typeof p?.deductible === 'boolean' ? p.deductible : row.deductible,
      deductibleReason: p?.deductibleReason || row.deductibleReason,
      supplier: p?.vendor || row.supplier,
      invoiceNo: p?.invoiceNo || row.invoiceNo,
      customerInfoIssues: (p?.customerInfoIssues && p.customerInfoIssues.length) ? p.customerInfoIssues : row.customerInfoIssues,
    });

    // Route the FIRST receipt of a job to the row its QR was started from, but only
    // if the target belongs to THIS job (guards against a newer scan clobbering it).
    // `consumed` is a synchronous ref so a batch of receipts in one poll tick still
    // routes only the first to the target row and appends the rest as new rows.
    const t = scanTargetRef.current;
    let targetId: string;
    let targetRow: LiquidationRow;
    if (t && t.pairingToken === jobToken && !t.consumed && t.rowId) {
      targetId = t.rowId;
      t.consumed = true;
      const nextRows = rowsRef.current.map((row) => (row.id === targetId ? applyParsed(row) : row));
      targetRow = nextRows.find((row) => row.id === targetId) || applyParsed(newPhysicalRow('', ''));
      rowsRef.current = nextRows;
      setRows(nextRows);
    } else {
      const nr = applyParsed(newPhysicalRow('', ''));
      targetId = nr.id;
      targetRow = nr;
      const nextRows = [...rowsRef.current, nr];
      rowsRef.current = nextRows;
      setRows(nextRows);
    }

    const rec: ReceiptAttachment = {
      id: `rcpt-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      rowId: targetId,
      filename: r.filename,
      thumbnailDataUrl: r.thumbnailDataUrl || undefined,
      oneDriveId: r.oneDriveId,
      webUrl: r.webUrl,
      uploadedAt: new Date().toISOString(),
      uploadStatus: 'done',
    };
    setReceipts((prev) => [...prev, rec]);
    const lowConf = typeof p?.confidence === 'number' && p.confidence < 0.5;
    setScanSnackbar({
      open: true,
      severity: lowConf ? 'warning' : 'success',
      message: lowConf
        ? 'Receipt received — low confidence, please verify amount, date & category.'
        : `Receipt received: ${r.filename}`,
    });
    // Phone-delivered receipts only carry a small thumbnail (no full-res image
    // base64 reaches the browser), so the hash is best-effort and coarser than
    // the ScanBatch path's. Fires after the row/receipt state above so the
    // duplicate check (delayed by the hash computation) sees the new row.
    void (async () => {
      let hash: string | undefined;
      if (r.thumbnailDataUrl) {
        hash = await computeImageHash(r.thumbnailDataUrl);
        if (hash) setReceipts((prev) => prev.map((x) => (x.id === rec.id ? { ...x, imageHash: hash } : x)));
      }
      // Re-read targetRow off rowsRef in case other rows/edits landed while the hash
      // was being computed, but fall back to the row captured at receipt-arrival time.
      const latestTargetRow = rowsRef.current.find((r) => r.id === targetId) || targetRow;
      await checkRowForDuplicates(latestTargetRow, hash || undefined);
    })();
  };

  // Poll the active phone scan job for delivered receipts. Runs at the PAGE level
  // (not in the QR dialog) so receipts keep arriving even after the dialog is closed,
  // letting the user "scan another" on the phone without re-pairing.
  useEffect(() => {
    if (!activeScanJob || !token) return;
    let cancelled = false;
    scanJobInFlightRef.current = false;
    const id = setInterval(async () => {
      if (scanJobInFlightRef.current) return;
      scanJobInFlightRef.current = true;
      try {
        const since = activeScanJob.received;
        const res = await fetch(`${API_BASE}/api/scan-jobs/${activeScanJob.pairingToken}?since=${since}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({ ok: false, results: [], total: since }));
        if (!cancelled && res.ok && data.ok && Array.isArray(data.results) && data.results.length > 0) {
          (data.results as DeliveredReceipt[]).forEach((item) => handlePhoneReceipt(item, activeScanJob.pairingToken));
          const newTotal = typeof data.total === 'number' ? data.total : since + data.results.length;
          setActiveScanJob((prev) => (prev && prev.pairingToken === activeScanJob.pairingToken ? { ...prev, received: newTotal } : prev));
        }
      } catch { /* transient */ } finally {
        scanJobInFlightRef.current = false;
      }
    }, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeScanJob, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = () => ({
    form_no: formNo,
    date_of_submission: dateOfSubmission,
    employee_name: employeeName,
    employee_number: employeeNumber,
    rows_json: rows.map((r) => ({
      id: r.id,
      physicalItemNo: r.physicalItemNo,
      date: r.date,
      category: r.category,
      projectId: r.projectId,
      projectName: r.projectName,
      projectNo: r.projectNo,
      particulars: r.particulars,
      amount: r.amount,
      remarks: r.remarks,
      deductible: r.deductible,
      deductibleReason: r.deductibleReason ?? null,
      supplier: r.supplier ?? '',
      invoiceNo: r.invoiceNo ?? '',
      customerInfoIssues: r.customerInfoIssues ?? [],
    })),
    total_amount: totalAmount,
    ca_id: caId || null,
    // Only successfully uploaded receipts persist — local-only files can't be
    // recovered after a reload anyway.
    receipts_json: receipts
      .filter((r) => r.uploadStatus === 'done' && r.oneDriveId)
      .map(({ file: _f, uploadStatus: _s, ...keep }) => keep),
    removed_receipt_ids: Array.from(removedReceiptIdsRef.current),
  });

  const saveDraft = async () => {
    if (!token) return;
    if (!receiptsReadyToSave()) return;
    setSaving(true);
    setSubmitSuccess(null);
    try {
      const body = { ...payload(), status: 'draft' };
      const url = draftId ? `${API_BASE}/api/liquidations/${draftId}` : `${API_BASE}/api/liquidations`;
      const method = draftId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        // The server keeps stored receipts this tab didn't know about; show them.
        const known = new Set(receiptsRef.current.map((r) => r.id));
        const kept: ReceiptAttachment[] = (Array.isArray(data.receipts) ? data.receipts : [])
          .filter((r: ReceiptAttachment) => r && r.id && !known.has(r.id))
          .map((r: ReceiptAttachment) => ({ ...r, uploadStatus: 'done' as const }));
        if (kept.length) setReceipts((prev) => [...prev, ...kept]);
        markReceiptsSaved([...receiptsRef.current.filter((r) => r.uploadStatus === 'done' && r.oneDriveId), ...kept]);
        const newId = data.id ?? draftId;
        setDraftId(newId);
        if (newId) setLoadedOptionValue(`draft:${newId}`);
        setSubmitSuccess('Draft saved.');
        fetch(`${API_BASE}/api/liquidations`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.json())
          .then((d) => {
            if (d.success && d.liquidations) {
              setDrafts(d.liquidations.filter((l: { status: string }) => l.status === 'draft'));
              setSubmittedLiquidations(d.liquidations.filter((l: { status: string }) => l.status === 'submitted').sort((a: { created_at: number }, b: { created_at: number }) => (b.created_at || 0) - (a.created_at || 0)));
            }
          })
          .catch(() => {});
      } else {
        setSubmitSuccess(data.error || 'Save failed');
      }
    } catch (e) {
      setSubmitSuccess(e instanceof Error ? e.message : 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const loadDraft = async (id: string, isSubmitted = false): Promise<LiquidationRow[] | null> => {
    if (!token) return null;
    const res = await fetch(`${API_BASE}/api/liquidations/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json().catch(() => ({}));
    if (!data.success || !data.liquidation) return null;
    const l = data.liquidation;
    if (l.physical_item_numbering_error) { setSubmitSuccess(l.physical_item_numbering_error); return null; }
    setReviewContentHash(l.receipt_review_content_hash || '');
    setReviewLocked(!!l.receipt_review_locked);
    const serverStatus = String(l.status ?? '').toLowerCase().trim();
    const submitted =
      isSubmitted ||
      serverStatus === 'submitted';
    setIsViewingSubmitted(submitted);
    setFormNo(l.form_no || '');
    setDateOfSubmission(l.date_of_submission || new Date().toISOString().slice(0, 10));
    setEmployeeName(l.employee_name || '');
    setEmployeeNumber(l.employee_number || '');
    setCaId(l.ca_id || '');
    let raw: Record<string, unknown>[] = [];
    try {
      raw = Array.isArray(l.rows_json)
        ? l.rows_json
        : typeof l.rows_json === 'string'
          ? JSON.parse(l.rows_json || '[]')
          : [];
    } catch {
      raw = [];
    }
    if (!Array.isArray(raw)) raw = [];
    nextPhysicalItemNoRef.current = 1;
    const loadedRows: LiquidationRow[] =
      raw.length === 0
        ? [newPhysicalRow('', '')]
        : raw.map((r: Record<string, unknown>) => {
            const base = newRow(String(r.projectName ?? ''), String(r.projectNo ?? ''));
            const pidValue = r.projectId;
            // Firestore IDs are strings; accept any non-empty value as-is
            const projectId: string | '' = pidValue !== undefined && pidValue !== null && pidValue !== '' ? String(pidValue) : '';
            return {
              id: typeof r.id === 'string' && r.id ? String(r.id) : base.id,
              physicalItemNo: typeof r.physicalItemNo === 'number' ? r.physicalItemNo : undefined,
              date: String(r.date ?? new Date().toISOString().slice(0, 10)),
              category: String(r.category ?? ''),
              projectId,
              projectName: String(r.projectName ?? ''),
              projectNo: String(r.projectNo ?? ''),
              particulars: String(r.particulars ?? ''),
              amount: Number(r.amount) || 0,
              remarks: String(r.remarks ?? ''),
              // Older drafts have no deductible flag — default to true (deductible).
              deductible: typeof r.deductible === 'boolean' ? r.deductible : true,
              deductibleReason: typeof r.deductibleReason === 'string' ? r.deductibleReason : undefined,
              supplier: String(r.supplier ?? ''),
              invoiceNo: String(r.invoiceNo ?? ''),
              customerInfoIssues: Array.isArray(r.customerInfoIssues) ? (r.customerInfoIssues as string[]) : undefined,
            };
          });
    const physicalRows = withPhysicalItemNumbers(loadedRows);
    nextPhysicalItemNoRef.current = Math.max(l.physical_item_numbering?.nextItemNo || 1,
      ...physicalRows.map(row => (row.physicalItemNo || 0) + 1));
    setSortConfig(DEFAULT_ROW_SORT);
    setRows(physicalRows);
    let loadedReceipts: ReceiptAttachment[] = [];
    try {
      const rawReceipts = Array.isArray(l.receipts_json)
        ? l.receipts_json
        : typeof l.receipts_json === 'string'
          ? JSON.parse(l.receipts_json || '[]')
          : [];
      if (Array.isArray(rawReceipts)) {
        loadedReceipts = rawReceipts
          .filter((r: Record<string, unknown>) => r && typeof r === 'object')
          .map((r: Record<string, unknown>) => ({
            id: String(r.id ?? `rcpt-${Math.random().toString(36).slice(2)}`),
            rowId: String(r.rowId ?? ''),
            filename: String(r.filename ?? 'receipt'),
            oneDriveId: r.oneDriveId ? String(r.oneDriveId) : undefined,
            webUrl: r.webUrl ? String(r.webUrl) : undefined,
            thumbnailDataUrl: r.thumbnailDataUrl ? String(r.thumbnailDataUrl) : undefined,
            uploadedAt: r.uploadedAt ? String(r.uploadedAt) : undefined,
            uploadStatus: 'done' as const,
          }));
      }
    } catch {
      loadedReceipts = [];
    }
    setReceipts(loadedReceipts);
    markReceiptsSaved(loadedReceipts);
    setDraftId(submitted ? null : l.id);
    setLoadedOptionValue(submitted ? `submitted:${l.id}` : `draft:${l.id}`);
    setLoadedReimb(
      submitted && l.reimbursement_status
        ? { id: l.id, status: l.reimbursement_status, at: l.reimbursed_at || null, caId: l.ca_id || null }
        : null
    );
    setRevisionMode(false);
    const pr = l.pending_revision;
    if (submitted && pr && typeof pr === 'object') {
      let prRows: LiquidationRow[] = [];
      try {
        const rawPr = typeof pr.rows_json === 'string' ? JSON.parse(pr.rows_json || '[]') : pr.rows_json;
        if (Array.isArray(rawPr)) {
          prRows = rawPr.map((r: Record<string, unknown>) => ({
            ...newRow(String(r.projectName ?? ''), String(r.projectNo ?? '')),
            id: typeof r.id === 'string' && r.id ? r.id : `row-${Math.random().toString(36).slice(2)}`,
            physicalItemNo: typeof r.physicalItemNo === 'number' ? r.physicalItemNo : physicalRows.find(row => row.id === r.id)?.physicalItemNo,
            date: String(r.date ?? ''),
            category: String(r.category ?? ''),
            projectId: r.projectId != null && r.projectId !== '' ? String(r.projectId) : '',
            particulars: String(r.particulars ?? ''),
            amount: Number(r.amount) || 0,
            remarks: String(r.remarks ?? ''),
          }));
        }
      } catch {
        prRows = [];
      }
      setPendingRevision({
        rows: prRows,
        totalAmount: Number(pr.total_amount) || 0,
        proposedBy: String(pr.proposed_by ?? ''),
        proposedByName: pr.proposed_by_name ? String(pr.proposed_by_name) : null,
        proposedAt: Number(pr.proposed_at) || 0,
        note: pr.note ? String(pr.note) : null,
      });
    } else {
      setPendingRevision(null);
    }
    setSubmitSuccess(null);
    return physicalRows;
  };

  useEffect(() => {
    if (!token || focusedFinanceOrigin?.type !== 'liquidation') return;
    let cancelled = false;
    setFinanceFocusLoading(true);
    setFinanceFocusError('');
    loadDraft(focusedFinanceOrigin.id, false)
      .then((loadedRows) => {
        if (cancelled) return;
        if (!loadedRows) {
          setFinanceFocusError('Liquidation could not be loaded or is not visible to this account.');
          return;
        }
        if (focusedFinanceOrigin.rowId === '__form__') return;
        if (!loadedRows.some((row) => row.id === focusedFinanceOrigin.rowId)) {
          setFinanceFocusError('The liquidation was loaded, but the linked itemized row no longer exists.');
          return;
        }
        window.requestAnimationFrame(() => {
          financeRowRefs.current.get(financeFocusToken(focusedFinanceOrigin))?.scrollIntoView({
            behavior: 'smooth', block: 'center', inline: 'nearest',
          });
        });
      })
      .catch(() => {
        if (!cancelled) setFinanceFocusError('Failed to load the linked liquidation.');
      })
      .finally(() => {
        if (!cancelled) setFinanceFocusLoading(false);
      });
    return () => { cancelled = true; };
    // loadDraft intentionally reads the latest form setters; the stable URL token is
    // the trigger, avoiding reloads on every row edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawFinanceFocus, token]);

  const clearFinanceFocus = () => {
    const params = new URLSearchParams(location.search);
    params.delete('focus');
    params.delete('from');
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' }, { replace: true });
  };

  const backToFinanceSource = () => {
    const from = new URLSearchParams(location.search).get('from');
    if (from) navigate(from);
  };

  // Admin: flip the reimbursement status of the loaded no-CA liquidation.
  const updateReimbursement = async (next: 'pending' | 'reimbursed') => {
    if (!token || !loadedReimb) return;
    try {
      const res = await fetch(`${API_BASE}/api/liquidations/${loadedReimb.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reimbursement_status: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setLoadedReimb((prev) => prev && { ...prev, status: next, at: next === 'reimbursed' ? Math.floor(Date.now() / 1000) : null });
        setSubmitSuccess(next === 'reimbursed' ? 'Marked as reimbursed.' : 'Reverted to pending reimbursement.');
        refreshLiquidationsList();
      } else {
        setSubmitSuccess(data.error || 'Update failed');
      }
    } catch (e) {
      setSubmitSuccess(e instanceof Error ? e.message : 'Network error');
    }
  };

  // The submitted liquidation being revised in place (revision mode keeps
  // loadedOptionValue pointing at it while the form unlocks).
  const revisionTargetId = loadedOptionValue.startsWith('submitted:') ? loadedOptionValue.slice('submitted:'.length) : null;

  const refreshCashAdvances = () => {
    if (!token) return;
    fetch(`${API_BASE}/api/cash-advances`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => d.success && d.cash_advances && setCashAdvances(d.cash_advances))
      .catch(() => {});
  };

  const enterRevisionMode = () => {
    setRevisionMode(true);
    setIsViewingSubmitted(false);
    setSubmitSuccess(null);
  };

  const cancelRevision = () => {
    if (revisionTargetId) loadDraft(revisionTargetId, true);
  };

  // Submit the current form state as a proposed edit to the loaded submitted
  // liquidation. Superadmins get it applied immediately (server decides).
  const submitRevision = async () => {
    if (!token || !revisionTargetId) return;
    if (rows.length === 0) {
      setSubmitSuccess('Add at least one row.');
      return;
    }
    if (!receiptsReadyToSave()) return;
    setSaving(true);
    setSubmitSuccess(null);
    try {
      const p = payload();
      const res = await fetch(`${API_BASE}/api/liquidations/${revisionTargetId}/propose-edit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          rows_json: p.rows_json,
          receipts_json: p.receipts_json,
          total_amount: p.total_amount,
          employee_name: p.employee_name,
          date_of_submission: p.date_of_submission,
          removed_receipt_ids: p.removed_receipt_ids,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        await loadDraft(revisionTargetId, true);
        refreshLiquidationsList();
        refreshCashAdvances();
        setSubmitSuccess(data.applied ? 'Edit applied to the submitted liquidation.' : 'Edit submitted for superadmin approval.');
      } else {
        setSubmitSuccess(data.error || 'Failed to submit edit');
      }
    } catch (e) {
      setSubmitSuccess(e instanceof Error ? e.message : 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const resolveRevision = async (action: 'approve' | 'reject') => {
    if (!token || !revisionTargetId) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/liquidations/${revisionTargetId}/revision/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        await loadDraft(revisionTargetId, true);
        refreshLiquidationsList();
        refreshCashAdvances();
        setSubmitSuccess(action === 'approve' ? 'Edit approved and applied.' : 'Edit rejected.');
      } else {
        setSubmitSuccess(data.error || `Failed to ${action} edit`);
      }
    } catch (e) {
      setSubmitSuccess(e instanceof Error ? e.message : 'Network error');
    } finally {
      setSaving(false);
    }
  };

  // Row-level summary of what a pending revision changes, for the approval banner.
  const revisionDiff = React.useMemo(() => {
    if (!pendingRevision) return null;
    const curById = new Map(rows.map((r) => [r.id, r]));
    const propById = new Map(pendingRevision.rows.map((r) => [r.id, r]));
    const added = pendingRevision.rows.filter((r) => !curById.has(r.id));
    const removed = rows.filter((r) => !propById.has(r.id));
    const changed = pendingRevision.rows
      .map((prop) => ({ prop, cur: curById.get(prop.id) }))
      .filter((x): x is { prop: LiquidationRow; cur: LiquidationRow } =>
        !!x.cur && (
          Number(x.cur.amount) !== Number(x.prop.amount) ||
          x.cur.particulars !== x.prop.particulars ||
          x.cur.date !== x.prop.date ||
          x.cur.category !== x.prop.category ||
          String(x.cur.projectId) !== String(x.prop.projectId)
        ));
    return { added, removed, changed };
  }, [pendingRevision, rows]);

  const refreshLiquidationsList = () => {
    if (!token) return;
    fetch(`${API_BASE}/api/liquidations`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.liquidations) {
          setDrafts(d.liquidations.filter((l: { status: string }) => l.status === 'draft'));
          setSubmittedLiquidations(d.liquidations.filter((l: { status: string }) => l.status === 'submitted').sort((a: { created_at: number }, b: { created_at: number }) => (b.created_at || 0) - (a.created_at || 0)));
        }
      })
      .catch(() => {});
  };

  const handleDeleteLiquidation = async () => {
    const id = liquidationToDeleteId;
    if (!token || id == null || String(id).trim() === '') return;
    setIsDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/api/liquidations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      let data: { success?: boolean; error?: string } = {};
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }
      if (data.success) {
        const idToRemove = id;
        setDrafts((prev) => prev.filter((d) => d.id !== idToRemove));
        setSubmittedLiquidations((prev) => prev.filter((d) => d.id !== idToRemove));
        setDeleteDialogOpen(false);
        setDraftId(null);
        setLoadedOptionValue('');
        setIsViewingSubmitted(false);
        setRevisionMode(false);
        setPendingRevision(null);
        setSortConfig(DEFAULT_ROW_SORT);
        nextPhysicalItemNoRef.current = 1;
        setRows([newPhysicalRow('', '')]);
        setFormNo('');
        setEmployeeName(user?.full_name?.trim() || user?.username || '');
        setEmployeeNumber('');
        setDateOfSubmission(new Date().toISOString().slice(0, 10));
        setCaId('');
        setReceipts([]);
        markReceiptsSaved([]);
        setLoadedReimb(null);
        setSubmitSuccess('Liquidation deleted.');
        refreshLiquidationsList();
        fetch(`${API_BASE}/api/cash-advances`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.json())
          .then((d) => {
            if (d.success && d.cash_advances) setCashAdvances(d.cash_advances);
          })
          .catch(() => {});
      } else if (res.status === 404) {
        const idToRemove = id;
        setDrafts((prev) => prev.filter((d) => d.id !== idToRemove));
        setSubmittedLiquidations((prev) => prev.filter((d) => d.id !== idToRemove));
        setDeleteDialogOpen(false);
        setDraftId(null);
        setLoadedOptionValue('');
        setIsViewingSubmitted(false);
        setRevisionMode(false);
        setPendingRevision(null);
        setSortConfig(DEFAULT_ROW_SORT);
        nextPhysicalItemNoRef.current = 1;
        setRows([newPhysicalRow('', '')]);
        setFormNo('');
        setEmployeeName(user?.full_name?.trim() || user?.username || '');
        setEmployeeNumber('');
        setDateOfSubmission(new Date().toISOString().slice(0, 10));
        setCaId('');
        setReceipts([]);
        setLoadedReimb(null);
        setSubmitSuccess('Liquidation removed from list.');
        refreshLiquidationsList();
      } else {
        const errMsg = data.error || (res.status === 401 ? 'Unauthorized' : res.status === 403 ? 'Forbidden' : `Delete failed (${res.status})`);
        setSubmitSuccess(errMsg);
      }
    } catch (e) {
      setSubmitSuccess(e instanceof Error ? e.message : 'Network error');
    } finally {
      setIsDeleting(false);
    }
  };

  const submitLiquidation = async () => {
    if (!token) return;
    if (rows.length === 0) {
      setSubmitSuccess('Add at least one row.');
      return;
    }
    if (!receiptsReadyToSave()) return;
    setSaving(true);
    setSubmitSuccess(null);
    try {
      // formNo is only a preview; the server assigns the real number on submit
      // (and moves draft receipts into that number's folder) and returns both.
      const body = { ...payload(), status: 'submitted' };
      const url = draftId ? `${API_BASE}/api/liquidations/${draftId}` : `${API_BASE}/api/liquidations`;
      const method = draftId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        const finalFormNo: string = data.form_no || formNo;
        const finalReceipts: ReceiptAttachment[] = Array.isArray(data.receipts) ? data.receipts : receipts;
        addLiquidationRowsToProjectExpenses(rows, data.id ?? draftId ?? undefined, finalFormNo, caId || undefined, finalReceipts).catch(() => {});
        setDraftId(null);
        setLoadedOptionValue('');
        setIsViewingSubmitted(false);
        setSortConfig(DEFAULT_ROW_SORT);
        nextPhysicalItemNoRef.current = 1;
        setRows([newPhysicalRow('', '')]);
        setFormNo('');
        setSubmitSuccess(caId
          ? `Liquidation ${finalFormNo} submitted. Amount applied to CA has been deducted; expenses added to project expense.`
          : `Liquidation ${finalFormNo} submitted as an out-of-pocket reimbursement claim (pending payback); expenses added to project expense.`);
        setCaId('');
        setReceipts([]);
        markReceiptsSaved([]);
        // Fetch next form number for new form
        fetch(`${API_BASE}/api/liquidations/next-form-no`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.json())
          .then((d) => {
            if (d.success && d.form_no) {
              setFormNo(d.form_no);
            }
          })
          .catch(() => {});
        fetch(`${API_BASE}/api/liquidations`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.json())
          .then((d) => {
            if (d.success && d.liquidations) {
              setDrafts(d.liquidations.filter((l: { status: string }) => l.status === 'draft'));
              setSubmittedLiquidations(d.liquidations.filter((l: { status: string }) => l.status === 'submitted').sort((a: { created_at: number }, b: { created_at: number }) => (b.created_at || 0) - (a.created_at || 0)));
            }
          })
          .catch(() => {});
        fetch(`${API_BASE}/api/cash-advances`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.json())
          .then((d) => d.success && d.cash_advances && setCashAdvances(d.cash_advances))
          .catch(() => {});
      } else {
        setSubmitSuccess(data.error || 'Submit failed');
      }
    } catch (e) {
      setSubmitSuccess(e instanceof Error ? e.message : 'Network error');
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    const row = newPhysicalRow('', '');
    setRows((prev) => [...prev, row]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    // Receipts belong to their row — drop them with it.
    markReceiptsRemoved(receiptsRef.current.filter((r) => r.rowId === id));
    setReceipts((prev) => prev.filter((r) => r.rowId !== id));
  };

  const updateRow = (id: string, field: keyof LiquidationRow, value: string | number | boolean) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  const setRowProject = (id: string, projectId: string | '') => {
    if (projectId === '') {
      updateRow(id, 'projectId', '');
      updateRow(id, 'projectName', '');
      updateRow(id, 'projectNo', '');
      return;
    }
    const p = projects.find((x) => String(x.id) === projectId);
    if (!p) return;
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              projectId,
              projectName: p.project_name || '',
              projectNo: p.po_number || p.project_no || '',
            }
          : r
      )
    );
  };

  const totalAmount = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);

  const selectedCa = cashAdvances.find((c) => c.id === caId);
  // Usable for new liquidations: approved with money left. The currently
  // selected CA is always included so loaded liquidations keep their label.
  const availableCAs = cashAdvances.filter(
    (c) => (c.status === 'approved' && Number(c.balance_remaining) > 0) || c.id === caId
  );
  const caLabel = (c: CaOption) =>
    `${c.ca_no || `CA #${c.id}`}${c.project_name ? ` — ${c.project_name}` : c.purpose ? ` — ${c.purpose}` : ''}`;
  // In revision mode the original total is already deducted from the CA, so
  // the plain balance check would false-positive — the server validates the
  // delta against the live balance instead.
  const overBalance = !isViewingSubmitted && !revisionMode && !!selectedCa && totalAmount > Number(selectedCa.balance_remaining);
  const splitCaCovered = selectedCa ? Math.min(totalAmount, Number(selectedCa.balance_remaining)) : totalAmount;
  const splitReimbursable = selectedCa ? Math.max(0, totalAmount - Number(selectedCa.balance_remaining)) : 0;

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const margin = 14;
    const pageWidth = 210;
    const pageHeight = 297;
    const footerY = pageHeight - 10;
    const headerY = 14;
    let y = headerY;

    // Load Arial Narrow font
    const hasArialNarrow = typeof arialNarrowBase64 === 'string' && arialNarrowBase64.length > 0;
    if (hasArialNarrow) {
      doc.addFileToVFS('ArialNarrow.ttf', arialNarrowBase64);
      doc.addFont('ArialNarrow.ttf', 'ArialNarrow', 'normal');
      doc.addFont('ArialNarrow.ttf', 'ArialNarrow', 'bold');
    }
    const fontBody = () => doc.setFont(hasArialNarrow ? 'ArialNarrow' : 'helvetica', 'normal');
    const fontBold = () => doc.setFont(hasArialNarrow ? 'ArialNarrow' : 'helvetica', 'bold');

    // Load logo once for reuse on all pages
    let logoDataUrl: string | null = null;
    let logoWidth = 0;
    let logoHeight = 0;
    try {
      const { loadLogoTransparentBackground, IOCT_ICON_LOGO_PDF_SIZE } = await import('../utils/logoUtils');
      const logoUrl = `${process.env.PUBLIC_URL || ''}/logo-ioct-only.png`;
      logoDataUrl = await loadLogoTransparentBackground(logoUrl);
      logoWidth = IOCT_ICON_LOGO_PDF_SIZE;
      logoHeight = IOCT_ICON_LOGO_PDF_SIZE;
    } catch (_) {}

    const lineHeight = 5;
    // Draw header on first page (logo, company name, Liquidation Form + Date on right)
    const drawHeader = () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, 'PNG', margin, headerY - 2, logoWidth, logoHeight);
        doc.setFontSize(9);
        fontBold();
        doc.text('IO Control Technologie OPC', margin, headerY + logoHeight + 4);
      }
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold'); // Use Arial Bold (helvetica bold) for title
      doc.text('Liquidation Form', pageWidth - margin, headerY, { align: 'right' });
      fontBody();
      doc.setFontSize(10);
      doc.text(`Date: ${dateOfSubmission || '—'}`, pageWidth - margin, headerY + lineHeight, { align: 'right' });
    };

    drawHeader();
    y += logoHeight > 0 ? logoHeight + 10 : 6;
    fontBody();
    doc.setFontSize(10);
    doc.text(`Name: ${(employeeName || '—').trim()}`, margin, y);
    y += 10;
    const exportRows = sortLiquidationRows(rows);
    const body = exportRows.length > 0
      ? exportRows.map((r, i) => [
          String(r.physicalItemNo ?? '—'),
          r.date || '—',
          r.category || '—',
          (r.projectName || '—').slice(0, 25),
          r.projectNo || '—',
          (r.particulars || '—').slice(0, 30),
          Number(r.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 }),
          (r.remarks || '—').slice(0, 15),
        ])
      : [['—', '—', '—', '—', '—', '—', '0.00', '—']];

    const tableStartY = y;
    const rowsPerPage = 25;
    const bottomReserve = 60;
    const tableHead = [['Item #', 'Date', 'Category', 'Project', 'PO #', 'Particulars', 'Amount', 'Remarks']];
    const tableOpts = {
      margin: { left: margin, right: margin, bottom: bottomReserve },
      tableWidth: pageWidth - margin * 2,
      styles: { fontSize: 7, font: hasArialNarrow ? 'ArialNarrow' : 'helvetica', cellPadding: 2 },
      headStyles: { fillColor: [44, 90, 160] as [number, number, number], font: hasArialNarrow ? 'ArialNarrow' : 'helvetica', fontStyle: 'bold' as const, fontSize: 7, cellPadding: 2 },
    };

    const chunks: string[][][] = [];
    for (let i = 0; i < body.length; i += rowsPerPage) {
      chunks.push(body.slice(i, i + rowsPerPage));
    }
    if (chunks.length === 0) chunks.push([['—', '—', '—', '—', '—', '—', '0.00', '—']]);

    for (let i = 0; i < chunks.length; i++) {
      if (i > 0) {
        doc.addPage();
        drawHeader();
      }
      autoTable(doc, {
        head: tableHead,
        body: chunks[i],
        startY: tableStartY,
        ...tableOpts,
      });
    }
    const docWithTable = doc as jsPDF & { lastAutoTable?: { finalY: number } };
    y = (docWithTable.lastAutoTable?.finalY ?? tableStartY) + 8;
    // Total on the right side - use Helvetica Bold
    doc.setFont('helvetica', 'bold');
    doc.text(`Total: ${totalAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}`, pageWidth - margin, y, { align: 'right' });
    if (caId) {
      doc.text(`Applied to: ${selectedCa?.ca_no || `CA #${caId}`}`, margin, y);
    } else {
      const reimbursedSuffix = loadedReimb?.status === 'reimbursed'
        ? ` — Reimbursed${loadedReimb.at ? ` on ${new Date(loadedReimb.at * 1000).toLocaleDateString()}` : ''}`
        : '';
      doc.text(`For Reimbursement (out-of-pocket)${reimbursedSuffix}`, margin, y);
    }
    y += 14;
    fontBody();
    doc.setFontSize(9);
    const sigY = footerY - 28;
    doc.text('Prepared by:', margin, sigY);
    const preparedByName = (employeeName || user?.full_name || user?.username || user?.email || '—').trim() || '—';
    doc.text(preparedByName, margin, sigY + 6);

    // ── Receipt attachments appendix ──────────────────────────────────────
    // Image receipts embed in a 2-column grid so the signed PDF carries its
    // own proof; non-image files (PDFs) are listed by name. Sources: the
    // in-memory file from this session, else the stored OneDrive copy.
    let missingReceipts = 0;
    if (receipts.length > 0) {
      let odToken: string | null = null;
      let driveId: string | null = null;
      if (isCorporateOneDriveConfigured() && oneDriveSignedIn) {
        try {
          odToken = await getOneDriveToken();
          if (odToken) driveId = await resolveCorporateDriveId(odToken);
        } catch { odToken = null; driveId = null; }
      }
      const entries: { caption: string; dataUrl: string | null }[] = [];
      for (const r of receipts) {
        const caption = `${receiptRowLabel(exportRows, r.rowId)} · ${r.filename}`;
        if (!isImageReceipt(r)) {
          entries.push({ caption: `${caption} (file — see OneDrive)`, dataUrl: null });
          continue;
        }
        try {
          let blob: Blob | null = r.file ?? null;
          if (!blob && r.oneDriveId && odToken && driveId) {
            blob = await fetchDriveItemBlob(odToken, driveId, r.oneDriveId);
          }
          if (!blob) throw new Error('no source for receipt image');
          entries.push({ caption, dataUrl: await normalizeImageOrientation(blob) });
        } catch {
          missingReceipts++;
          entries.push({ caption: `${caption} (could not be embedded)`, dataUrl: null });
        }
      }
      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text('Receipt Attachments', margin, 18);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const gridGap = 6;
      const cellW = (pageWidth - margin * 2 - gridGap) / 2;
      const maxImgH = 85;
      let gy = 26;
      let col = 0;
      const textLines: string[] = [];
      for (const e of entries) {
        if (!e.dataUrl) { textLines.push(e.caption); continue; }
        if (col === 0 && gy + maxImgH + 10 > footerY - 6) { doc.addPage(); gy = 18; }
        const x = margin + col * (cellW + gridGap);
        try {
          const props = doc.getImageProperties(e.dataUrl);
          const scale = Math.min(cellW / props.width, maxImgH / props.height);
          doc.addImage(e.dataUrl, 'JPEG', x, gy, props.width * scale, props.height * scale);
        } catch { /* unreadable image — caption still printed */ }
        doc.text(e.caption, x, gy + maxImgH + 4, { maxWidth: cellW });
        col = col === 0 ? 1 : 0;
        if (col === 0) gy += maxImgH + 10;
      }
      if (textLines.length > 0) {
        let ty = col === 1 ? gy + maxImgH + 10 : gy;
        if (ty + textLines.length * 5 > footerY - 6) { doc.addPage(); ty = 18; }
        for (const line of textLines) { doc.text(`• ${line}`, margin, ty); ty += 5; }
      }
      fontBody();
      doc.setFontSize(8);
    }

    // Add footer with page number and Doc No. (LQ-YY-NNN-AAA) on all pages
    const totalPages = doc.getNumberOfPages();
    const docNo = formNoIsFinal ? liquidationDocNo(formNo) : `${liquidationDocNo('')} (not yet submitted)`;
    doc.setFontSize(8);
    for (let p = 1; p <= totalPages; p++) {
      doc.setPage(p);
      doc.text(`Page ${p} of ${totalPages}`, pageWidth - margin, footerY, { align: 'right' });
      doc.text(`Doc No.: ${docNo}`, margin, footerY);
    }
    
    // Filename names the CA (and therefore the project) the liquidation
    // settles against, e.g. LQ-0005_IOCT2606001-CA01.pdf — or flags it as an
    // out-of-pocket reimbursement claim.
    const fnBase = formNoIsFinal ? (formNo || 'form') : 'DRAFT';
    doc.save(
      caId
        ? (selectedCa?.ca_no
            ? `${fnBase}_${selectedCa.ca_no}.pdf`
            : `Liquidation_${fnBase}_${dateOfSubmission.replace(/-/g, '')}.pdf`)
        : `${fnBase}_Reimbursement.pdf`
    );
    if (missingReceipts > 0) {
      setSubmitSuccess(`${missingReceipts} receipt(s) could not be embedded in the PDF — sign in to OneDrive to include stored receipts.`);
    }
  };

  const handleExport = () => {
    const wb = XLSX.utils.book_new();
    const headerRow1 = ['Employee Name', employeeName, 'Employee Number', employeeNumber];
    const headerRow2 = ['Date of Submission', dateOfSubmission, 'Form No.', formNo];
    const tableHeaders = ['Item #', 'Date', 'Category', 'Project Name', 'PO #', 'Particulars', 'Amount', 'Remarks'];
    const exportRows = sortLiquidationRows(rows);
    const dataRows = exportRows.map((r, i) => [
      r.physicalItemNo ?? '—',
      r.date,
      r.category,
      r.projectName,
      r.projectNo,
      r.particulars,
      r.amount,
      r.remarks,
    ]);
    const wsData = [headerRow1, headerRow2, [], tableHeaders, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    // A4 print: paperSize 9 = A4, fitToPage so content fits one A4 sheet
    ws['!pageSetup'] = { paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 0 };
    XLSX.utils.book_append_sheet(wb, ws, 'Liquidation');
    const filename = `liquidation_${formNoIsFinal ? formNo : 'DRAFT'}_${dateOfSubmission.replace(/-/g, '')}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = ev.target?.result;
        if (data == null) return;
        const wb =
          typeof data === 'string'
            ? XLSX.read(data, { type: 'string' })
            : XLSX.read(new Uint8Array(data as ArrayBuffer), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (raw.length < 4) return;
        const r0 = (raw[0] as unknown[]) || [];
        const r1 = (raw[1] as unknown[]) || [];
        if (r0[1] != null) setEmployeeName(String(r0[1]));
        if (r0[3] != null) setEmployeeNumber(String(r0[3]));
        if (r1[1] != null) setDateOfSubmission(String(r1[1]).slice(0, 10));
        if (r1[3] != null) setFormNo(String(r1[3]));
        const headerRow = raw[3] as unknown[];
        if (!headerRow || (headerRow[0] !== 'No.' && headerRow[0] !== 'Item #' && headerRow[0] !== 1)) return;
        const imported: LiquidationRow[] = [];
        for (let i = 4; i < raw.length; i++) {
          const row = raw[i] as unknown[];
          if (!row || row.length < 6) continue;
          const date = row[1] != null ? String(row[1]).slice(0, 10) : new Date().toISOString().slice(0, 10);
          const amount = typeof row[6] === 'number' ? row[6] : parseFloat(String(row[6] || 0)) || 0;
          const fromSameForm = String((raw[1] as unknown[])?.[3] || '') === formNo;
          const existing = fromSameForm ? rows.find(existingRow => existingRow.physicalItemNo === Number(row[0])) : undefined;
          imported.push(existing ? { ...existing } : newPhysicalRow(String(row[3] ?? ''), String(row[4] ?? '')));
          const last = imported[imported.length - 1];
          last.projectId = '';
          last.date = date;
          last.category = String(row[2] ?? '');
          last.particulars = String(row[5] ?? '');
          last.amount = amount;
          last.remarks = String(row[7] ?? '');
        }
        if (imported.length > 0) setRows(imported);
      } catch (err) {
        console.error('Import error:', err);
      }
      e.target.value = '';
    };
    if (file.name.toLowerCase().endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const theme = {
    primary: '#2c5aa0',
    primaryLight: '#4f7bc8',
    secondary: '#1e4a72',
    border: '#e0e0e0',
    paper: '#ffffff',
  };

  return (
    <Box sx={{ p: 3, width: '100%', minHeight: 'calc(100vh - 80px)', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: theme.primary }}>
          Liquidation Form
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={saveDraft}
            disabled={saving || isViewingSubmitted || revisionMode}
            sx={{ borderColor: theme.primary, color: theme.primary }}
          >
            Save draft
          </Button>
          {canDeleteLiquidation && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              sx={{ borderColor: 'error.main', color: 'error.main' }}
            >
              Delete
            </Button>
          )}
          {(drafts.length > 0 || submittedLiquidations.length > 0) && (
            <Select<string>
              size="small"
              displayEmpty
              value={loadedOptionValue}
              onChange={(e) => {
                const raw = e.target.value as string;
                const unsavedReceipts = receiptsRef.current.filter((r) => r.uploadStatus === 'uploading'
                  || (r.uploadStatus === 'done' && !savedReceiptIdsRef.current.has(r.id))).length;
                if (unsavedReceipts > 0 && !window.confirm(`${unsavedReceipts} receipt${unsavedReceipts === 1 ? ' is' : 's are'} uploaded but not saved to this form. Switch anyway? (Cancel, then Save Draft, to keep them linked.)`)) return;
                if (raw === '') {
                  setIsViewingSubmitted(false);
                  setRevisionMode(false);
                  setPendingRevision(null);
                  setDraftId(null);
                  setLoadedOptionValue('');
                  setSortConfig(DEFAULT_ROW_SORT);
                  nextPhysicalItemNoRef.current = 1;
                  setRows([newPhysicalRow('', '')]);
                  setFormNo('');
                  setEmployeeName('');
                  setEmployeeNumber('');
                  setDateOfSubmission(new Date().toISOString().slice(0, 10));
                  setCaId('');
                  setReceipts([]);
                  markReceiptsSaved([]);
                  setLoadedReimb(null);
                  setSubmitSuccess(null);
                  return;
                }
                const colonIdx = raw.indexOf(':');
                const id = colonIdx !== -1 ? raw.slice(colonIdx + 1) : '';
                const isSubmitted = raw.startsWith('submitted:');
                if (id) loadDraft(id, isSubmitted);
              }}
              sx={{ minWidth: 200, '& .MuiSelect-select': { py: 0.75 } }}
              renderValue={(v: string) => {
                if (v === '') return 'Load liquidation…';
                const id = v.includes(':') ? v.split(':')[1] : '';
                const item = [...drafts, ...submittedLiquidations].find((d) => d.id === id);
                return item?.form_no || `#${id}`;
              }}
            >
              <MenuItem value="">
                <em>Load liquidation…</em>
              </MenuItem>
              {drafts.length > 0 && [
                <MenuItem key="drafts-header" disabled sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Drafts
                </MenuItem>,
                ...drafts.map((d) => (
                  <MenuItem key={`draft-${d.id}`} value={`draft:${d.id}`}>
                    {d.form_no || `#${d.id}`} – {d.date_of_submission || 'no date'} (₱{Number(d.total_amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })})
                  </MenuItem>
                )),
              ]}
              {submittedLiquidations.length > 0 && [
                <MenuItem key="submitted-header" disabled sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', mt: drafts.length > 0 ? 1 : 0 }}>
                  Submitted
                </MenuItem>,
                ...submittedLiquidations.map((d) => (
                  <MenuItem key={`submitted-${d.id}`} value={`submitted:${d.id}`}>
                    {d.form_no || `#${d.id}`} – {d.date_of_submission || 'no date'} (₱{Number(d.total_amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })})
                    {(() => {
                      const chip = workflowChip(d);
                      if (!chip) return null;
                      const el = (
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: chip.color, color: '#fff' }}
                        />
                      );
                      return chip.tip ? <Tooltip title={chip.tip}>{el}</Tooltip> : el;
                    })()}
                  </MenuItem>
                )),
              ]}
            </Select>
          )}
          <Button
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            onClick={() => setScanDialog({ open: true, rowId: null })}
            disabled={isViewingSubmitted}
            sx={{ borderColor: theme.primary, color: theme.primary, '&:hover': { borderColor: theme.secondary, color: theme.secondary } }}
          >
            Scan Receipt
          </Button>
          <Button
            variant="outlined"
            startIcon={isScanningOne ? <CircularProgress size={18} /> : <UploadFileIcon />}
            onClick={() => scanOneInputRef.current?.click()}
            disabled={isViewingSubmitted || isScanningOne}
            sx={{ borderColor: theme.primary, color: theme.primary, '&:hover': { borderColor: theme.secondary, color: theme.secondary } }}
          >
            Scan One
          </Button>
          <Button
            variant="outlined"
            startIcon={<PhotoLibraryIcon />}
            onClick={() => setScanBatchOpen(true)}
            disabled={isViewingSubmitted}
            sx={{ borderColor: theme.primary, color: theme.primary, '&:hover': { borderColor: theme.secondary, color: theme.secondary } }}
          >
            Scan Multiple
          </Button>
          <input
            type="file"
            ref={scanOneInputRef}
            accept={RECEIPT_FILE_ACCEPT}
            style={{ display: 'none' }}
            onChange={handleScanOneFileChange}
          />
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => fileInputRef.current?.click()}
            sx={{ borderColor: theme.primary, color: theme.primary, '&:hover': { borderColor: theme.secondary, color: theme.secondary } }}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            onClick={exportToPDF}
            sx={{ borderColor: theme.primary, color: theme.primary }}
          >
            Export to PDF (for signing)
          </Button>
          <Button
            variant="contained"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.secondary } }}
          >
            Export
          </Button>
        </Box>
      </Box>
      {(rawFinanceFocus || financeFocusError) && (
        <Alert
          severity={financeFocusError ? 'warning' : 'info'}
          sx={{ mb: 2 }}
          action={(
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {new URLSearchParams(location.search).get('from') && (
                <Button color="inherit" size="small" onClick={backToFinanceSource}>Back to source</Button>
              )}
              <Button color="inherit" size="small" onClick={clearFinanceFocus}>Clear focus</Button>
            </Box>
          )}
        >
          {financeFocusLoading
            ? 'Loading the exact liquidation item…'
            : financeFocusError || 'Showing the exact liquidation item from the money trail.'}
        </Alert>
      )}
      {loadedLiquidationId && (caId || loadedReimb) && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {caId && (
            <Chip
              label="Open linked cash advance"
              color="primary"
              variant="outlined"
              onClick={() => navigate(financeFocusUrl(
                { type: 'cash_advance', id: caId },
                `${location.pathname}${location.search}`,
              ))}
              sx={{ cursor: 'pointer' }}
            />
          )}
          {loadedReimb && (
            <Chip
              label="Open linked reimbursement"
              color="info"
              variant="outlined"
              onClick={() => navigate(financeFocusUrl(
                { type: 'reimbursement', id: loadedReimb.id },
                `${location.pathname}${location.search}`,
              ))}
              sx={{ cursor: 'pointer' }}
            />
          )}
        </Box>
      )}
      {submitSuccess && (
        <Typography variant="body2" sx={{ mb: 2, color: submitSuccess.startsWith('Liquidation submitted') ? 'success.main' : submitSuccess.startsWith('Draft saved') ? 'info.main' : 'error.main' }}>
          {submitSuccess}
        </Typography>
      )}
      <input
        type="file"
        ref={fileInputRef}
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={handleImport}
      />

      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.border}`,
          borderRadius: 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          backgroundColor: theme.paper,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          <Box sx={{ flex: '1 1 200px' }}>
            <TextField
              fullWidth
              size="small"
              label="Name"
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              disabled={isViewingSubmitted}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
            />
          </Box>
          <Box sx={{ flex: '1 1 140px' }}>
            <TextField
              fullWidth
              size="small"
              label="Employee Number"
              value={employeeNumber}
              onChange={(e) => setEmployeeNumber(e.target.value)}
              disabled={isViewingSubmitted}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
            />
          </Box>
          <Box sx={{ flex: '1 1 160px' }}>
            <TextField
              fullWidth
              size="small"
              label="Date of Submission"
              type="date"
              value={dateOfSubmission}
              onChange={(e) => setDateOfSubmission(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={isViewingSubmitted}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
            />
          </Box>
          <Box sx={{ flex: '0 1 120px' }}>
            <TextField
              fullWidth
              size="small"
              label={formNoIsFinal ? 'Form No.' : 'Form No. (preview)'}
              value={formNoIsFinal ? formNo : (livePreviewNo || formNo)}
              disabled
              helperText={formNoIsFinal ? undefined : 'Assigned on submit'}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
            />
          </Box>
          {(availableCAs.length > 0 || caId !== '') && (
            <Box sx={{ flex: '1 1 260px' }}>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={caId === '' ? '' : caId}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '' || v == null) setCaId('');
                  else setCaId(String(v));
                }}
                disabled={isViewingSubmitted}
                sx={{ bgcolor: 'background.paper', minHeight: 40 }}
                renderValue={(v) => {
                  if ((v as unknown) === '' || v == null) return 'Apply to CA (optional)';
                  const ca = cashAdvances.find((c) => c.id === v);
                  return ca ? caLabel(ca) : `CA #${v}`;
                }}
              >
                <MenuItem value="">
                  <em>No CA — out-of-pocket (reimbursement)</em>
                </MenuItem>
                {availableCAs.map((ca) => (
                  <MenuItem key={ca.id} value={ca.id}>
                    {caLabel(ca)} · Balance ₱{Number(ca.balance_remaining).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                  </MenuItem>
                ))}
              </Select>
              {selectedCa && !isViewingSubmitted && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: overBalance ? 'error.main' : 'text.secondary' }}>
                  {overBalance
                    ? `₱${splitReimbursable.toLocaleString('en-PH', { minimumFractionDigits: 2 })} of this will exceed the CA and become a reimbursement claim`
                    : `₱${Number(selectedCa.balance_remaining).toLocaleString('en-PH', { minimumFractionDigits: 2 })} remaining to liquidate on this CA`}
                </Typography>
              )}
              {!selectedCa && caId === '' && !isViewingSubmitted && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                  No CA selected — this will be tracked as a reimbursement claim.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {overBalance && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Total ₱{totalAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })} exceeds the CA balance ₱{Number(selectedCa?.balance_remaining || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}. The covered portion (₱{splitCaCovered.toLocaleString('en-PH', { minimumFractionDigits: 2 })}) will draw down the CA; the remaining ₱{splitReimbursable.toLocaleString('en-PH', { minimumFractionDigits: 2 })} will be tracked as a reimbursement claim payable to you.
          </Alert>
        )}

        {isViewingSubmitted && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ color: 'info.dark', fontWeight: 500 }}>
                {reviewLocked && !isAdmin
                  ? 'Reviewed by RJ/Admin — this liquidation is locked. Ask an admin for any changes.'
                  : 'Viewing submitted liquidation (read-only). Use “Propose edit” to change it in place.'}
              </Typography>
              {loadedReimb && (
                <Chip
                  size="small"
                  label={loadedReimb.status === 'reimbursed'
                    ? `Reimbursed${loadedReimb.at ? ` on ${new Date(loadedReimb.at * 1000).toLocaleDateString()}` : ''}`
                    : 'Reimbursement pending'}
                  color={loadedReimb.status === 'reimbursed' ? 'success' : 'warning'}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {loadedReimb && isAdmin && !loadedReimb.caId && (
                <Button
                  variant="outlined"
                  size="small"
                  color={loadedReimb.status === 'reimbursed' ? 'warning' : 'success'}
                  onClick={() => updateReimbursement(loadedReimb.status === 'reimbursed' ? 'pending' : 'reimbursed')}
                >
                  {loadedReimb.status === 'reimbursed' ? 'Revert to pending' : 'Mark reimbursed after RJ review'}
                </Button>
              )}
              {loadedReimb && isAdmin && loadedReimb.caId && loadedReimb.status !== 'reimbursed' && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                  Settle this from the Reimbursements page
                </Typography>
              )}
              {!(reviewLocked && !isAdmin) && (
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                onClick={enterRevisionMode}
                sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.secondary } }}
              >
                {user?.role === 'superadmin' ? 'Edit (applies immediately)' : 'Propose edit'}
              </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setDraftId(null);
                  setLoadedOptionValue('');
                  setIsViewingSubmitted(false);
                  setPendingRevision(null);
                  setLoadedReimb(null);
                  setSubmitSuccess(null);
                }}
                sx={{ borderColor: 'info.main', color: 'info.dark' }}
              >
                Create editable copy
              </Button>
            </Box>
          </Box>
        )}

        {loadedLiquidationId && loadedOptionValue.startsWith('submitted:') && (
          <LiquidationReceiptReview key={`${loadedLiquidationId}:${reviewContentHash}`} liquidationId={loadedLiquidationId} contentHash={reviewContentHash}
            readOnly={revisionMode || !!pendingRevision} onReload={() => loadDraft(loadedLiquidationId, true)} />
        )}

        {revisionMode && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Editing submitted liquidation {formNo || ''} in place.{' '}
              {user?.role === 'superadmin'
                ? 'Your changes apply immediately on submit.'
                : 'Your changes take effect only after a superadmin approves them.'}
            </Typography>
            <Button variant="outlined" size="small" color="inherit" onClick={cancelRevision}>
              Cancel edit
            </Button>
          </Box>
        )}

        {isViewingSubmitted && pendingRevision && revisionDiff && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Edit pending approval — proposed by {pendingRevision.proposedByName || 'unknown'}
                {pendingRevision.proposedAt ? ` on ${new Date(pendingRevision.proposedAt * 1000).toLocaleDateString()}` : ''}:
                {' '}total ₱{totalAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })} → ₱{pendingRevision.totalAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
              </Typography>
              {user?.role === 'superadmin' && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Button variant="contained" size="small" color="success" disabled={saving} onClick={() => resolveRevision('approve')}>
                    Approve
                  </Button>
                  <Button variant="outlined" size="small" color="error" disabled={saving} onClick={() => resolveRevision('reject')}>
                    Reject
                  </Button>
                </Box>
              )}
            </Box>
            {pendingRevision.note && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>Note: {pendingRevision.note}</Typography>
            )}
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {revisionDiff.changed.map(({ cur, prop }) => (
                <Typography key={`chg-${prop.id}`} component="li" variant="body2">
                  “{prop.particulars || cur.particulars || '(no particulars)'}”: ₱{Number(cur.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })} → ₱{Number(prop.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                  {cur.date !== prop.date ? ` · date ${cur.date} → ${prop.date}` : ''}
                </Typography>
              ))}
              {revisionDiff.added.map((r) => (
                <Typography key={`add-${r.id}`} component="li" variant="body2">
                  Added: “{r.particulars || '(no particulars)'}” ₱{Number(r.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                </Typography>
              ))}
              {revisionDiff.removed.map((r) => (
                <Typography key={`rem-${r.id}`} component="li" variant="body2">
                  Removed: “{r.particulars || '(no particulars)'}” ₱{Number(r.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                </Typography>
              ))}
              {revisionDiff.changed.length === 0 && revisionDiff.added.length === 0 && revisionDiff.removed.length === 0 && (
                <Typography component="li" variant="body2">Header-only changes (name/date/receipts).</Typography>
              )}
            </Box>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {isCorporateOneDriveConfigured() ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                variant="outlined"
                icon={oneDriveSignedIn ? <CloudDoneIcon /> : <CloudOffIcon />}
                label={oneDriveSignedIn
                  ? 'OneDrive connected — receipts will be stored'
                  : 'OneDrive not signed in — receipts won’t be stored'}
                color={oneDriveSignedIn ? 'success' : 'warning'}
              />
              {!oneDriveSignedIn && (
                <Button size="small" variant="outlined" onClick={() => oneDriveLogin()} sx={{ borderColor: theme.primary, color: theme.primary }}>
                  Sign in to OneDrive
                </Button>
              )}
            </Box>
          ) : (
            <span />
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addRow}
            disabled={isViewingSubmitted}
            sx={{
              bgcolor: theme.primary,
              '&:hover': { bgcolor: theme.secondary },
            }}
          >
            Add row
          </Button>
        </Box>

        {activeScanJob && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={<Button color="inherit" size="small" onClick={() => setActiveScanJob(null)}>Stop</Button>}
          >
            Receiving receipts from phone… {activeScanJob.received} received. Scan more on your phone — rows are added automatically.
          </Alert>
        )}
        {isPhone ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {rows.length > 1 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">Sort</Typography>
                {(['date', 'amount'] as const).map((k) => (
                  <Button key={k} size="small" variant={sortConfig?.key === k ? 'contained' : 'outlined'} onClick={() => handleSort(k)}
                    sx={{ textTransform: 'capitalize', minWidth: 0 }}>
                    {k}{sortConfig?.key === k ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}
                  </Button>
                ))}
              </Box>
            )}
            {rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                Tap "Add row" to add expense lines. Select a project per row from the list.
              </Typography>
            ) : (
              sortedRows.map((row, index) => {
                const origin = loadedLiquidationId ? liquidationRowOrigin(loadedLiquidationId, row) : null;
                const rowToken = origin ? financeFocusToken(origin) : '';
                const focused = Boolean(
                  origin
                  && focusedFinanceOrigin?.type === 'liquidation'
                  && focusedFinanceOrigin.id === loadedLiquidationId
                  && focusedFinanceOrigin.rowId === row.id,
                );
                return (
                  <LiquidationRowCard
                    key={row.id}
                    row={row}
                    index={index}
                    projects={projects}
                    lockedRow={isViewingSubmitted}
                    receiptCount={receipts.filter((r) => r.rowId === row.id).length}
                    focused={focused}
                    primary={theme.primary}
                    rowRef={(element) => {
                      if (!rowToken) return;
                      if (element) financeRowRefs.current.set(rowToken, element as unknown as HTMLTableRowElement);
                      else financeRowRefs.current.delete(rowToken);
                    }}
                    onChange={updateRow}
                    onProject={setRowProject}
                    onScan={(id) => setScanDialog({ open: true, rowId: id })}
                    onAttach={(id) => { receiptRowIdRef.current = id; receiptInputRef.current?.click(); }}
                    onRemove={removeRow}
                    moneyTrail={origin ? <MoneyTrailButton origin={origin} compact /> : undefined}
                  />
                );
              })
            )}
          </Box>
        ) : (
        <TableContainer sx={{ border: `1px solid ${theme.border}`, borderRadius: 1, flex: 1, minHeight: 0, overflow: 'auto', overscrollBehaviorY: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: theme.primary + '12' }}>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, width: 48 }}>Item #</TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 110 }} sortDirection={sortConfig?.key === 'date' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig?.key === 'date'}
                    direction={sortConfig?.key === 'date' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('date')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 120 }}>
                  Category
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 180 }}>
                  Project Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 120 }}>
                  PO #
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 360 }}>
                  Particulars
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 100 }} sortDirection={sortConfig?.key === 'amount' ? sortConfig.direction : false}>
                  <TableSortLabel
                    active={sortConfig?.key === 'amount'}
                    direction={sortConfig?.key === 'amount' ? sortConfig.direction : 'asc'}
                    onClick={() => handleSort('amount')}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 100 }}>
                  Remarks
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 140 }}>
                  Supplier
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: theme.primary, minWidth: 110 }}>
                  Invoice No.
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, color: theme.primary, width: 90 }}>
                  <Tooltip title="Deductible: tick if this is an allowable / tax-deductible business expense supported by a valid BIR receipt. AI suggests this on scan; accounting can override.">
                    <span>Deductible</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Click "Add row" to add expense lines. Select a project per row from the list.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map((row, index) => {
                  const origin = loadedLiquidationId ? liquidationRowOrigin(loadedLiquidationId, row) : null;
                  const rowToken = origin ? financeFocusToken(origin) : '';
                  const focused = Boolean(
                    origin
                    && focusedFinanceOrigin?.type === 'liquidation'
                    && focusedFinanceOrigin.id === loadedLiquidationId
                    && focusedFinanceOrigin.rowId === row.id,
                  );
                  return (
                  <TableRow
                    key={row.id}
                    ref={(element: HTMLTableRowElement | null) => {
                      if (!rowToken) return;
                      if (element) financeRowRefs.current.set(rowToken, element);
                      else financeRowRefs.current.delete(rowToken);
                    }}
                    hover
                    aria-current={focused ? 'true' : undefined}
                    sx={{
                      '&:hover .delete-btn': { opacity: 1 },
                      '& > .MuiTableCell-root': { verticalAlign: 'top' },
                      ...(focused ? {
                        bgcolor: 'rgba(44,90,160,0.14)',
                        outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px',
                      } : {}),
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary' }}>{row.physicalItemNo ?? '—'}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="date"
                        value={row.date}
                        onChange={(e) => updateRow(row.id, 'date', e.target.value)}
                        disabled={isViewingSubmitted}
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        sx={{ maxWidth: 140, '& .MuiInputBase-input': { py: 0.75 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={row.category}
                        displayEmpty
                        onChange={(e) => updateRow(row.id, 'category', e.target.value)}
                        disabled={isViewingSubmitted}
                        sx={{ minWidth: 130, '& .MuiSelect-select': { py: 0.75 } }}
                      >
                        <MenuItem value="">
                          <em>Select</em>
                        </MenuItem>
                        {LIQUIDATION_CATEGORIES.map((c) => (
                          <MenuItem key={c} value={c}>
                            {c}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select<string | ''>
                        size="small"
                        value={row.projectId === '' ? '' : String(row.projectId)}
                        displayEmpty
                        onChange={(e) => {
                          const val = e.target.value;
                          setRowProject(row.id, val === '' ? '' : String(val));
                        }}
                        disabled={isViewingSubmitted}
                        sx={{ minWidth: 200, width: '100%', '& .MuiSelect-select': { py: 0.75 } }}
                        renderValue={(v: string | '') => {
                          if (v === '' || v === undefined) return <em>Select project</em>;
                          const p = projects.find((x) => String(x.id) === v);
                          return p?.project_name || row.projectName || '—';
                        }}
                      >
                        <MenuItem value="">
                          <em>Select project</em>
                        </MenuItem>
                        {projects.map((p) => (
                          <MenuItem key={p.id} value={String(p.id)}>
                            {p.project_name || `Project ${p.id}`}
                            {(p.po_number || p.project_no) ? ` (${p.po_number || p.project_no})` : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.projectNo}
                        onChange={(e) => updateRow(row.id, 'projectNo', e.target.value)}
                        placeholder="PO #"
                        fullWidth
                        sx={{ '& .MuiInputBase-input': { py: 0.75 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.particulars}
                        onChange={(e) => updateRow(row.id, 'particulars', e.target.value)}
                        placeholder="Particulars"
                        multiline
                        minRows={3}
                        fullWidth
                        sx={{
                          minWidth: 360,
                          '& .MuiInputBase-root': { py: 1 },
                          '& textarea': { overflowWrap: 'anywhere', lineHeight: 1.5 },
                        }}
                        inputProps={{ 'aria-label': `Particulars for item ${row.physicalItemNo ?? '—'}` }}
                        InputProps={row.duplicateWarnings && row.duplicateWarnings.length ? {
                          endAdornment: (
                            <Tooltip title={`Possible duplicate receipt: ${row.duplicateWarnings.join(' ')}`}>
                              <WarningAmberIcon fontSize="small" color="warning" />
                            </Tooltip>
                          ),
                        } : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={row.amount || ''}
                        onChange={(e) =>
                          updateRow(row.id, 'amount', parseFloat(e.target.value) || 0)
                        }
                        fullWidth
                        sx={{ maxWidth: 110, '& .MuiInputBase-input': { py: 0.75 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.remarks}
                        onChange={(e) => updateRow(row.id, 'remarks', e.target.value)}
                        placeholder="Remarks"
                        fullWidth
                        sx={{ '& .MuiInputBase-input': { py: 0.75 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.supplier || ''}
                        onChange={(e) => updateRow(row.id, 'supplier', e.target.value)}
                        placeholder="Supplier"
                        fullWidth
                        sx={{ '& .MuiInputBase-input': { py: 0.75 } }}
                        InputProps={row.customerInfoIssues && row.customerInfoIssues.length ? {
                          endAdornment: (
                            <Tooltip title={`Buyer details on this receipt don't match IOCT: ${row.customerInfoIssues.join('; ')}`}>
                              <WarningAmberIcon fontSize="small" color="warning" />
                            </Tooltip>
                          ),
                        } : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.invoiceNo || ''}
                        onChange={(e) => updateRow(row.id, 'invoiceNo', e.target.value)}
                        placeholder="Invoice No."
                        fullWidth
                        sx={{ maxWidth: 120, '& .MuiInputBase-input': { py: 0.75 } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={row.deductibleReason || (row.deductible ? 'Marked deductible (allowable business expense)' : 'Not deductible')}>
                        <span>
                          <Checkbox
                            size="small"
                            checked={!!row.deductible}
                            onChange={(e) => updateRow(row.id, 'deductible', e.target.checked)}
                            disabled={isViewingSubmitted}
                            sx={{ color: theme.primary, '&.Mui-checked': { color: theme.primary } }}
                            inputProps={{ 'aria-label': 'Deductible' }}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {receipts.filter((r) => r.rowId === row.id).length === 0 && (
                        <Tooltip title="Scan receipt with phone">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setScanDialog({ open: true, rowId: row.id })}
                              disabled={isViewingSubmitted}
                              sx={{ color: theme.primary }}
                              aria-label="Scan receipt with phone"
                            >
                              <PhotoCameraIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Attach receipt (photo or PDF)">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              receiptRowIdRef.current = row.id;
                              receiptInputRef.current?.click();
                            }}
                            disabled={isViewingSubmitted}
                            sx={{ color: theme.primary }}
                            aria-label="Attach receipt"
                          >
                            <Badge
                              badgeContent={receipts.filter((r) => r.rowId === row.id).length}
                              color="primary"
                              overlap="circular"
                            >
                              <AttachFileIcon fontSize="small" />
                            </Badge>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => removeRow(row.id)}
                        className="delete-btn"
                        sx={{ opacity: { xs: 1, md: 0.5 }, color: 'error.main' }}
                        aria-label="Delete row"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      {origin && (
                        <MoneyTrailButton origin={origin} compact />
                      )}
                    </TableCell>
                  </TableRow>
                );})
              )}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        <input
          type="file"
          ref={receiptInputRef}
          accept="image/*,.pdf,.heic,.heif"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            const rid = receiptRowIdRef.current;
            if (rid) attachReceipts(rid, e.target.files);
            e.target.value = '';
          }}
        />

        {receipts.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.primary, mb: 1 }}>
              Receipts ({receipts.length})
            </Typography>
            {receipts.some((r) => r.uploadStatus === 'error') && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Some receipts could not be uploaded to OneDrive — they will still embed in the PDF this
                session, but won't be kept after a reload. Sign in to OneDrive and re-attach to store them.
              </Alert>
            )}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {receipts.map((r) => {
                const rowLabel = receiptRowLabel(sortedRows, r.rowId);
                return (
                  <Box
                    key={r.id}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, border: `1px solid ${theme.border}`, borderRadius: 1, p: 0.75, maxWidth: 280 }}
                  >
                    {r.thumbnailDataUrl ? (
                      // eslint-disable-next-line jsx-a11y/img-redundant-alt
                      <img
                        src={r.thumbnailDataUrl}
                        alt={r.filename}
                        onClick={() => openReceiptViewer(r)}
                        style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                      />
                    ) : (
                      <AttachFileIcon
                        onClick={() => openReceiptViewer(r)}
                        sx={{ fontSize: 32, color: 'text.secondary', cursor: (r.file || r.oneDriveId) ? 'pointer' : 'default' }}
                      />
                    )}
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {rowLabel}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150 }} title={r.filename}>
                        {r.filename}
                      </Typography>
                    </Box>
                    {r.uploadStatus === 'uploading' && <CircularProgress size={16} />}
                    {r.uploadStatus === 'error' && (
                      <Tooltip title="Not uploaded to OneDrive — sign in and re-attach to store permanently">
                        <ErrorOutlineIcon fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                    {r.webUrl && (
                      <IconButton size="small" onClick={() => window.open(r.webUrl, '_blank', 'noopener')} title="Open in OneDrive">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    )}
                    {!isViewingSubmitted && (
                      <IconButton size="small" color="error" onClick={() => removeReceipt(r.id)} title="Remove receipt">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {rows.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.primary }}>
              TOTAL AMOUNT: ± {totalAmount.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
            </Typography>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={revisionMode ? submitRevision : submitLiquidation}
              disabled={saving || isViewingSubmitted}
              sx={{ bgcolor: theme.secondary, '&:hover': { bgcolor: theme.primary } }}
            >
              {revisionMode
                ? (user?.role === 'superadmin' ? 'Apply edit' : 'Submit edit for approval')
                : 'Submit liquidation'}
            </Button>
          </Box>
        )}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete liquidation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this liquidation? This cannot be undone.
            {isViewingSubmitted && ' If it was applied to a cash advance, the balance will be restored.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteLiquidation} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <ScanToPhoneDialog
        open={scanDialog.open}
        onClose={() => setScanDialog((p) => ({ ...p, open: false }))}
        context={
          scanDialog.open
            ? {
                kind: 'liquidation',
                formNo: (formNo || 'draft').trim() || 'draft',
                year: scanYear,
                folderPath: scanFolderPath,
                rowId: scanDialog.rowId || '',
                label: scanDialog.rowId
                  ? `Form ${formNo || 'draft'} — ${receiptRowLabel(sortedRows, scanDialog.rowId)}`
                  : `Form ${formNo || 'draft'}`,
              }
            : null
        }
        onPaired={(pairingToken) => {
          scanTargetRef.current = { pairingToken, rowId: scanDialog.rowId || '', consumed: false };
          setActiveScanJob({ pairingToken, received: 0 });
        }}
      />
      {receiptCropQueue && <ReceiptUploadCropDialog file={receiptCropQueue.files[0]}
        onAttach={async file => { await attachReceiptFile(receiptCropQueue.rowId, file); advanceReceiptCrop(); }}
        onSkip={advanceReceiptCrop} onCancel={() => setReceiptCropQueue(null)} />}
      {/* Crop step between photo pick and AI parse for Scan One (auto-detected corners,
          adjustable). PDFs never reach this dialog — they parse directly. */}
      <Dialog open={!!scanOneEditUrl} onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Receipt Crop</DialogTitle>
        <DialogContent>
          {scanOneEditUrl && scanOneEditQuad && (
            <ReceiptCropper
              imageUrl={scanOneEditUrl}
              initialQuad={scanOneEditQuad}
              busy={isScanningOne}
              onConfirm={confirmScanOneCrop}
              onRetake={retakeScanOne}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={scanBatchOpen} onClose={() => setScanBatchOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Scan Multiple Receipts</DialogTitle>
        <DialogContent>
          {scanBatchOpen && (
            <ScanBatch
              mode="liquidation"
              selectedProject={null}
              categories={LIQUIDATION_CATEGORIES}
              onLiquidationItem={(item: LiquidationScanItem) => handleScanBatchItem(item)}
              onCancel={() => setScanBatchOpen(false)}
              onComplete={(n) => {
                setScanBatchOpen(false);
                setScanSnackbar({ open: true, severity: 'success', message: `Added ${n} receipt${n === 1 ? '' : 's'} as new row${n === 1 ? '' : 's'}.` });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={scanSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setScanSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setScanSnackbar((p) => ({ ...p, open: false }))}
          severity={scanSnackbar.severity}
          sx={{ width: '100%', boxShadow: 3 }}
        >
          {scanSnackbar.message}
        </Alert>
      </Snackbar>
      {receiptViewer && (
        <ReceiptViewer
          title={receiptViewer.receipt.filename}
          url={receiptViewer.url}
          webUrl={receiptViewer.receipt.webUrl}
          isPdf={/\.pdf$/i.test(receiptViewer.receipt.filename || '')}
          onClose={() => setReceiptViewer(null)}
          headerColor={theme.primary}
          onSaveRotation={(blob) => saveReceiptRotation(receiptViewer.receipt, blob)}
        />
      )}
    </Box>
  );
}
